import React, { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AIStudioDialog } from 'src/components/pages/aiStudio/aiStudioDialog';
import ProductCatalogDialog from 'src/components/pages/aiStudio/components/dialogs/productCatalogDialog';
import { ProductSideImage } from 'src/components/pages/aiStudio/types';
import GalleryImportBlockingOverlay from 'src/components/pages/images/galleryImportBlockingOverlay';
import { IMPORT_TARGET_BACKGROUND } from 'src/components/pages/images/libraryImportTasks';
import { ProductColor } from 'src/components/pages/productConfiguration/state/model/modelColor';
import ConnectModelDialog from 'src/components/pages/productConfiguration/steps/6-done/connectModelDialog';
import ConvertStandaloneProductDialog from 'src/components/pages/productConfiguration/steps/6-done/convertStandaloneProductDialog';
import DuplicateDialog from 'src/components/pages/productConfiguration/steps/6-done/duplicateDialog';
import { productListConnectModelMutation$data } from 'src/components/pages/products/__generated__/productListConnectModelMutation.graphql';
import { canCopyDemoProduct, useCopyDemoProduct } from 'src/components/pages/products/copyDemoProduct';
import MarketplacePreviewUrlDialog from 'src/components/pages/products/marketplacePreviewUrlDialog';
import { DialogOverlayConnect } from 'src/components/pages/products/productList';
import ProductSidePanel from 'src/components/pages/products/productSidebar';
import AddSalesChannelDialog from 'src/components/pages/salesChannels/addSalesChannelDialog';
import { SalesChannelNotAvailableDialogWindow } from 'src/components/pages/salesChannels/salesChannel';
import { TestPageSkeleton } from 'src/components/pages/testPage/testPageSkeleton';
import ChangeProductIcon from 'src/components/pages/userInterfaceSettings/icons/change-product.svg';
import DesktopWideIcon from 'src/components/pages/userInterfaceSettings/icons/desktop-wide.svg';
import DesktopIcon from 'src/components/pages/userInterfaceSettings/icons/desktop.svg';
import MobileIcon from 'src/components/pages/userInterfaceSettings/icons/mobile.svg';
import type { LivePreviewSize } from 'src/components/pages/userInterfaceSettings/livePreview/livePreview';
import mobileIphone380x844Skin from 'src/components/pages/userInterfaceSettings/livePreview/skins/mobile-iphone380x844.png';
import { ENV_TO_OLD_USER_INTERFACE_ID } from 'src/components/pages/userInterfaceSettings/settings';
import { Badge, CounterBadge } from 'src/components/widgets/badge';
import {
    FullscreenIframe,
    FullscreenIframeCloseButton,
    FullscreenIframeContent,
    FullscreenIframeFrameContainer,
    FullscreenIframeTitle,
    FullscreenIframeTopBar,
    FullscreenIframeTopBarCenter,
    FullscreenIframeTopBarLeft,
    FullscreenIframeTopBarRight,
    FullscreenLayout
} from 'src/components/widgets/fullscreenIframeLayout';
import { useRelayStore } from 'src/relay';
import config from 'src/shared/config';
import { withForcedNewInterface } from 'src/shared/configuratorUrl';
import { NoIntegrationDialog, useUpgradeDialogs } from 'src/shared/dialogs';
import { useFeatureFlag } from 'src/shared/feature-flags';
import { backofficeUrl, stringFormat, T, toLocalID, uuid } from 'src/shared/helpers';
import { productLogicName } from 'src/shared/productNames';
import { useAutoAddonActivation } from 'src/shared/hooks/useAutoAddonActivation';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import ProductDetailsIcon from 'src/shared/material_icons/action/info.svg';
import ExternalLinkIcon from 'src/shared/material_icons/action/open_in_new.svg';
import ArrowBackIcon from 'src/shared/material_icons/navigation/arrow_back.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import VerticalThreePoint from 'src/shared/material_icons/navigation/more_vert.svg';
import {
    getCustomizerTestOrderDetails,
    isComposerAddToCartMessage,
    isCustomizerAddToCartMessage,
    isCustomizerSpecializedAddToCartMessage,
    isDiscoveryAiGenerationCompletedMessage,
    isDiscoveryModeActivateToolMessage,
    isDiscoveryModeHiddenToolsMessage,
    isDiscoveryModeInteractiveMessage,
    isDiscoveryModeReadyMessage,
    isTestModeReadyMessage,
    postAddonsList,
    type ZakekeMessageDiscoveryModeUpdate
} from 'src/shared/zakekeMessage';
import styled, { css } from 'styled-components';
import { BasicButton, Link } from '../buttons';
import { tint } from '../buttons/buttonStyle';
import { MessageDialog, QuestionDialog, useDialogManager } from '../dialog';
import { DropdownActionItem, DropdownActions, DropdownDivider } from '../dropdown';
import { Icon } from '../icon';
import Loader from '../loader';
import { NotificationBanner } from '../notificationBanner';
import ShopifyAppEmbedBanner from '../shopifyAppEmbedBanner/shopifyAppEmbedBanner';
import {
    removeNotification,
    showErrorNotification,
    showInfoNotification,
    showNotification,
    showSuccessNotification,
    showWarningNotification
} from '../notificationToaster';
import {
    getShopifyDuplicationWarningText,
    getShopifyDuplicationWarningTitle,
    SHOPIFY_DUPLICATION_WARNING_LEARN_MORE_URL,
    SHOPIFY_INTEGRATION_SETTINGS_PATH
} from '../shopifyDuplicationWarning/shopifyDuplicationWarning';
import UpgradeDialog from '../upgradeDialog/upgradeDialog';
import WaitForProductExists from '../waitForProductExists';
import { ZSwitch } from '../zswitch';
import { ZTooltip } from '../ztooltip';
import { DiscoveryModePanel } from './DiscoveryModePanel';
import { testPageConnectModelMutation, testPageConnectModelMutation$data } from './__generated__/testPageConnectModelMutation.graphql';
import { testPageDeleteMutation } from './__generated__/testPageDeleteMutation.graphql';
import { testPageDuplicateMutation, testPageDuplicateMutation$data } from './__generated__/testPageDuplicateMutation.graphql';
import { testPageOrderMutation } from './__generated__/testPageOrderMutation.graphql';
import { testPageProductSetStatusMutation } from './__generated__/testPageProductSetStatusMutation.graphql';
import { testPageQuery } from './__generated__/testPageQuery.graphql';
import DiscoveryAiActivationBanner from './discoveryAiActivationBanner';
import DiscoveryAiCreditsPurchaseDialog from './discoveryAiCreditsPurchaseDialog';
import { DISCOVERY_AI_PAYLOADS } from './discoveryAiTools';
import { DiscoveryCollagePrefetch } from './discoveryCollagePrefetch';
import { DISCOVERY_COMMERCE_PAYLOADS } from './discoveryCommerceRules';
import DiscoveryFillBackgroundImportBanner from './discoveryFillBackgroundImportBanner';
import { DISCOVERY_PRICING_PAYLOADS, QUANTITY_TIERS_PRICING_RULE } from './discoveryPricingRules';
import { DISCOVERY_PRINTING_PAYLOADS } from './discoveryPrintMethods';
import { DISCOVERY_TOOLS_PAYLOADS } from './discoveryTools';
import DiscoveryVectorizeBanner from './discoveryVectorizeBanner';
import TestOrdersPurchaseDialog from './testOrdersPurchaseDialog';
import { DiscoveryAiCreditCase, DiscoveryAiCreditState, useDiscoveryAiCredits } from './useDiscoveryAiCredits';
import useDiscoveryBackgroundGallery from './useDiscoveryBackgroundGallery';

// Quantity presets for the discovery quantity-tiers control, derived from the tier breakpoints so the
// buttons always line up with where the per-unit price changes.
const DISCOVERY_QUANTITY_PRESETS = QUANTITY_TIERS_PRICING_RULE.tiers.map((tier) => tier.minQuantity);

const ChangeProductButton = styled.button<{ $discovery?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 9999px;
    background: ${(props) => (props.$discovery ? 'rgba(255, 255, 255, 0.15)' : '#f3f5f6')};
    color: ${(props) => (props.$discovery ? '#ffffff' : '#000000')};
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: ${(props) => (props.$discovery ? 'rgba(255, 255, 255, 0.25)' : '#f97316')};
        color: white;
    }

    ${Icon} {
        width: 22px;
        height: 22px;
        fill: currentColor;
        min-width: 0;
        min-height: 0;
    }
`;

const StyledBadge = styled(Badge)`
    margin-left: 0;
`;

const ClickableCounterBadge = styled(CounterBadge)`
    cursor: pointer;
`;

const BadgeContainer = styled.div`
    min-width: 110px;
`;

const BannerSection = styled.div`
    flex-shrink: 0;
    padding: 12px 24px 0;
`;

const TestModeShopifyAppEmbedBanner = styled(ShopifyAppEmbedBanner)`
    flex-shrink: 0;
    box-sizing: border-box;
    margin-top: 0;
    padding: 12px 24px 0;
`;

const PublishSwitchWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: -20px;
`;

const PublishSwitchLabel = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
`;

const ModeToggleContainer = styled.div`
    display: inline-flex;
    flex-shrink: 0;
    background: #e2e8f0;
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
`;

const ModeToggleButton = styled.button<{ $active: boolean; $variant?: 'test' | 'discovery' }>`
    min-width: 150px;
    padding: 6px 20px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    text-align: center;

    background: ${(props) => {
        if (!props.$active) return 'transparent';
        return props.$variant === 'discovery' ? 'linear-gradient(135deg, #E8650A 0%, #E6167F 100%)' : 'white';
    }};
    color: ${(props) => {
        if (!props.$active) return '#64748b';
        return props.$variant === 'discovery' ? '#ffffff' : '#1e293b';
    }};
    box-shadow: ${(props) => (props.$active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none')};

    &:hover {
        color: ${(props) => {
            if (!props.$active) return '#334155';
            return props.$variant === 'discovery' ? '#ffffff' : '#1e293b';
        }};
    }
`;

const UISettingsButton = styled.button`
    padding: 6px 14px;
    border: 1.5px solid rgba(255, 255, 255, 0.4);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        background: rgba(255, 255, 255, 0.25);
        border-color: rgba(255, 255, 255, 0.6);
    }
`;

// Serve i due ambienti di authoring, non il solo Composer: chi entra in prova ci entra da tutti e due.
// IL RITORNO ALL'EDITOR È NEL PRIMARIO PIENO DEGLI EDITOR, COME SAVE (utente, 2026-10-21): arancio del marchio scurito,
// testo bianco, un passo più scuro al passaggio (gli stessi valori di `editorPrimaryFill`). È l'azione principale di questa barra per chi viene dagli editor. Solo
// per i prodotti del configuratore: il Customizer esce con la sua X.
const BackToEditorButton = styled(BasicButton).attrs({ $size: 'medium' })`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
    border: 0;
    border-radius: 8px;
    background-color: ${(props) => tint(props.theme.app.secondary, -16)};
    color: #fff;
    font-weight: 500;

    &:hover:not([disabled]),
    &:active:not([disabled]) {
        background-color: ${(props) => tint(props.theme.app.secondary, -24)};
        color: #fff;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        min-width: 0;
        fill: currentColor;
    }
`;

type SkinOptions = {
    url: string;
    gutterTop: number;
    gutterBottom: number;
    gutterLeft: number;
    gutterRight: number;
};

const previewSizeConfigs: Record<LivePreviewSize, { width: number; height: number; skin: SkinOptions | null }> = {
    'desktop-wide': {
        width: window.innerWidth,
        height: window.innerHeight,
        skin: null
    },
    desktop: {
        width: 1220,
        height: 674,
        skin: null
    },
    mobile: {
        width: 380,
        height: 844,
        skin: {
            url: mobileIphone380x844Skin,
            gutterTop: 112,
            gutterBottom: 84,
            gutterLeft: 16,
            gutterRight: 16
        }
    }
};

const PreviewSkin = styled.div<{
    $width: number;
    $height: number;
    $scale: number;
    $skin?: SkinOptions | null;
}>`
    margin: auto;
    background: transparent;
    width: ${(props) => props.$width}px;
    height: ${(props) => props.$height}px;
    transform: scale(${(props) => props.$scale});
    transform-origin: center center;
    position: absolute;
    overflow: hidden;

    ${(props) =>
        !props.$skin &&
        css`
            border-radius: 16px;
            box-shadow:
                rgb(0, 0, 0, 0.15) 0 4px 20px 0,
                rgb(0, 0, 0, 0.15) 0 0 3px 0;
        `}

    ${(props) =>
        props.$skin &&
        css`
            padding-top: ${props.$skin.gutterTop}px;
            padding-bottom: ${props.$skin.gutterBottom}px;
            padding-left: ${props.$skin.gutterLeft}px;
            padding-right: ${props.$skin.gutterRight}px;

            &:after {
                content: ' ';
                position: absolute;
                inset: 0;
                background: url('${props.$skin.url}');
                z-index: 1;
                pointer-events: none;
                user-select: none;
            }
        `}
`;

const RoundIconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 9999px;
    background: #f3f5f6;
    color: #000000;
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: #f97316;
        color: white;
    }

    ${Icon} {
        width: 22px;
        height: 22px;
        fill: currentColor;
        min-width: 0;
        min-height: 0;
    }
`;

// The preview area mirrors the theme editor live preview: the content floats on the gray background
// with no white card, for every layout (desktop, laptop, mobile).
const PreviewFrameContainer = styled(FullscreenIframeFrameContainer)`
    && {
        background: transparent;
        box-shadow: none;
        border-radius: 0;
    }
`;

const usePreviewSkinConfig = (containerRef: React.RefObject<HTMLDivElement | null>, size: LivePreviewSize) => {
    const [config, setConfig] = useState({ width: 0, height: 0, scale: 1, skin: null as SkinOptions | null });
    const updateConfig = useCallback(() => {
        if (!containerRef.current) return;

        const { width, height, skin } = previewSizeConfigs[size];
        const scale = Math.min(1, (containerRef.current.clientWidth - 12) / width, (containerRef.current.clientHeight - 12) / height);

        setConfig((current) => {
            if (current.width === width && current.height === height && current.scale === scale && current.skin === skin) {
                return current;
            }

            return { width, height, scale, skin };
        });
    }, [containerRef, size]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(updateConfig);
        if (containerRef.current) resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, [containerRef, updateConfig]);

    useEffect(updateConfig, [updateConfig]);

    return config;
};

type PreviewModel = testPageQuery['response']['model'];

// Il pezzo di indirizzo che riconosce il 3D Scene Editor dentro un `returnTo`. Lo leggono in due:
// l'etichetta del tasto per tornare indietro e il parametro che dice al configuratore che sotto
// prova va messa la scena.
const SCENE_EDITOR_PAGE = '3D-product-configurator-scene-editor';

const setIframeSearchParam = (iframeUrl: string, key: string, value: string) => {
    try {
        const url = new URL(iframeUrl);
        url.searchParams.set(key, value);
        return url.toString();
    } catch {
        const [urlWithoutHash, hash] = iframeUrl.split('#', 2);
        const separator = urlWithoutHash.includes('?') ? '&' : '?';
        return `${urlWithoutHash}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}${hash ? `#${hash}` : ''}`;
    }
};

const removeIframeSearchParam = (iframeUrl: string, key: string) => {
    try {
        const url = new URL(iframeUrl);
        url.searchParams.delete(key);
        return url.toString();
    } catch {
        const [urlWithoutHash, hash] = iframeUrl.split('#', 2);
        const [base, query] = urlWithoutHash.split('?', 2);
        if (!query) {
            return iframeUrl;
        }
        const kept = query.split('&').filter((pair) => pair.length > 0 && pair.split('=')[0] !== key);
        return `${base}${kept.length > 0 ? `?${kept.join('&')}` : ''}${hash ? `#${hash}` : ''}`;
    }
};

// The one printing-method pill that is credit-gated (its own VECTORIZE pool). Must match the id in
// DiscoveryModePanel's PREDEFINED_PRINTING_METHODS and the key in DISCOVERY_PRINTING_PAYLOADS.
const AI_VECTORIZER_PILL = 'ai-vectorizer';

const EMPTY_DISCOVERY_SELECTIONS: Record<string, string | null> = {
    printing: null,
    pricing: null,
    tools: null,
    ai: null,
    commerce: null,
    modules: null
};

// Discovery selections persist across SPA navigation (Save → tool setup page → browser Back) so the merchant
// returns to the same Active tools. Module scope means a full page reload clears them — which is what we want,
// since a reload reboots the customizer iframe to a clean state. Keyed by product so selections don't leak
// across different products.
let discoverySelectionsCache: {
    productId: string;
    selections: Record<string, string | null>;
    quantity: number;
    entryCategory: string | null;
    entryTool: string | null;
} | null = null;

const TestPage: FC<{ productId: string; showCopyUrl?: boolean; isClassic?: boolean }> = ({ productId, showCopyUrl, isClassic }) => {
    // History
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const isPrototypeMode = searchParams.get('isPrototype') === '1';
    const { showPlanUpgradeDialog } = useUpgradeDialogs();
    const { showDialog, closeDialog } = useDialogManager();
    const { isAllowed, currentIAMUser } = usePermissions();
    const canAccessOrders = isAllowed(['ORDERS']);
    const canReadChannels = isAllowed(['SALES_CHANNELS']);
    const canReadStandaloneSites = isAllowed(['STANDALONE']);
    const canReadSubscription = isAllowed(['SUBSCRIPTION']);
    const [incrementStoreKey] = useRelayStore((state) => [state.incrementRelayStoreKey]);
    const { sellerMarketplace, subscription: sellerSubscription } = useSubscriptionData();
    const isDiscoveryModeEnabled = useFeatureFlag('discoveryMode');
    const isStandaloneEnabled = useFeatureFlag('standalone');
    const isStandaloneConversionEnabled = useFeatureFlag('standaloneProductConversion');
    const [hasResumed, setHasResumed] = useState(false);
    const [activeProductId, setActiveProductId] = useState(productId);
    const [showSidebar, setShowSidebar] = useState(false);

    const [fetchKey, setFetchKey] = useState(0);
    const [isPending, startTransition] = useTransition();
    const [discoveryMode, setDiscoveryMode] = useState(!isPrototypeMode && searchParams.get('mode') === 'discovery');
    const [isInfoBannerVisible, setIsInfoBannerVisible] = useState(() => localStorage.getItem('testPageInfoBannerDismissed') !== 'true');
    const [isShopifyDuplicationWarningVisible, setIsShopifyDuplicationWarningVisible] = useState(
        () => localStorage.getItem('testPageShopifyDuplicationWarningDismissed') !== 'true'
    );
    const [previewSize, setPreviewSize] = useState<LivePreviewSize>('desktop-wide');
    const effectivePreviewSize: LivePreviewSize = isPrototypeMode || discoveryMode ? 'desktop-wide' : previewSize;
    // Bumped to force the customizer iframe to remount and return to its initial state (e.g. after the
    // seller dismisses the "buy test orders" modal, which leaves the iframe stuck in its placing-order state).
    const [iframeReloadNonce, setIframeReloadNonce] = useState(0);
    const reloadIframe = useCallback(() => setIframeReloadNonce((nonce) => nonce + 1), []);
    const refreshProductIframe = useCallback(() => {
        startTransition(() => {
            setFetchKey((key) => key + 1);
            setIframeReloadNonce((nonce) => nonce + 1);
        });
    }, [startTransition]);
    const previewContainerRef = useRef<HTMLDivElement>(null);
    const skinConfig = usePreviewSkinConfig(previewContainerRef, effectivePreviewSize);
    const seedCategory = searchParams.get('discoveryCategory');
    const seedTool = searchParams.get('discoveryTool');
    const hasDiscoverySeed = Boolean(seedCategory && seedTool && Object.prototype.hasOwnProperty.call(EMPTY_DISCOVERY_SELECTIONS, seedCategory));
    // A new dashboard suggestion starts with the requested tool, rather than replaying another suggestion.
    // Returning to the same entry (Save → setup → Back), or without an explicit tool, still restores the sandbox.
    const canRestoreDiscovery =
        discoverySelectionsCache?.productId === productId &&
        (!hasDiscoverySeed || (discoverySelectionsCache.entryCategory === seedCategory && discoverySelectionsCache.entryTool === seedTool));
    // Per-category selections are restored from the in-memory cache when returning to the same product via SPA
    // navigation (e.g. after a Save redirect + browser Back), and re-applied to the customizer once it beacons
    // interactive again (see the replay effect below). A full page reload clears the cache, so selections reset
    // to empty and stay coherent with the freshly-booted iframe.
    const [discoverySelections, setDiscoverySelections] = useState<Record<string, string | null>>(() => {
        if (canRestoreDiscovery && discoverySelectionsCache) return discoverySelectionsCache.selections;
        // A deep link can pre-activate one tool in discovery (?discoveryCategory=…&discoveryTool=…);
        // seeding the selection makes the replay effect switch it on once the customizer is interactive.
        const seeded = { ...EMPTY_DISCOVERY_SELECTIONS };
        if (hasDiscoverySeed && seedCategory && seedTool) seeded[seedCategory] = seedTool;
        return seeded;
    });

    // Real Collage Layouts catalog (system grids), prefetched via DiscoveryCollagePrefetch while discovery mode is
    // active and streamed on the Collage Layouts click. A ref (not state) keeps handleDiscoverySelect's identity
    // stable and avoids a stale closure without re-creating the callback.
    const collagePayloadRef = useRef<object | null>(null);

    // --- Discovery AI credits (backoffice-owned gate) ---
    // The customizer has no GraphQL client, so the host owns the credit decision: when the seller engages an
    // AI Creative Tool we resolve the shared GENERATIVE_AI credit case and either open the real tool
    // (has-credits) or open a credit dialog first — activate the free trial (CASE 1) or buy a pack (CASE 3).
    const { resolve: resolveAiCredits, grantTrial } = useDiscoveryAiCredits();
    // The AI pill the seller engaged, held while a credit dialog is open so we can open the tool once credits
    // resolve to has-credits.
    const pendingAiToolRef = useRef<string | null>(null);
    // The resolved shared GENERATIVE_AI credit case, resolved when the seller presses an AI tool. Drives the
    // "billed via credits" activation banner (CASE 1) shown under the AI badges. Null until first resolved.
    const [aiCreditCase, setAiCreditCase] = useState<DiscoveryAiCreditCase | null>(null);
    const [isActivatingCredits, setIsActivatingCredits] = useState(false);

    // --- Discovery AI Vectorizer credits ---
    // The AI Vectorizer is a discovery PRINTING METHOD, not an AI tool, but it is billed the same way: its own
    // VECTORIZE pool, a one-time free trial, then paid packs. So it reuses the AI credit controller with the
    // pool swapped, and gets its own always-on banner (activate / balance / buy) on the Printing tab.
    const { resolve: resolveVectorizeCredits, grantTrial: grantVectorizeTrial } = useDiscoveryAiCredits('VECTORIZE');
    const [vectorizeCredits, setVectorizeCredits] = useState<DiscoveryAiCreditState | null>(null);
    const [isActivatingVectorizeCredits, setIsActivatingVectorizeCredits] = useState(false);
    // Whether the plan (or a Shopify dev store) rules the Vectorizer out entirely. Computed further down, after
    // the product query that provides the Shopify settings — so the engage callback below, which is defined
    // before it, reads it through a ref rather than closing over it.
    const isVectorizerUnavailableRef = useRef(false);

    const { resolveIsLegacy: resolveBgGalleryLegacy, startDefaultGalleryImport } = useDiscoveryBackgroundGallery();
    // Whether the seller is a legacy account with no background gallery at all — resolved when Fill Background is
    // engaged. Drives the "no background gallery yet" banner under the Tools pills. Null until first resolved.
    const [isBgGalleryLegacy, setIsBgGalleryLegacy] = useState<boolean | null>(null);
    // Bumped right after the import mutation is queued so the shared blocking overlay refetches immediately and
    // picks up the new background import task (rather than waiting for its next 4s poll).
    const [bgImportRefreshSignal, setBgImportRefreshSignal] = useState(0);

    // Stream a selection to the customizer iframe so it can apply it live (no reload). Each wired button maps
    // to a payload: printing → a raw print-type, pricing → a sample pricing rule. A null id resolves to a null
    // payload, which the customizer reads as "no override". The customizer's discovery contract only knows
    // 'tools' for tool-style payloads, so the backoffice-only 'modules' grouping (e.g. Get a Quote) streams
    // under 'tools' — otherwise the iframe wouldn't recognize the category and the update would be a no-op.
    // 'commerce' streams under its OWN category (the iframe knows it): remapping it onto 'tools' like modules
    // would make selecting Extra Options clobber an active Customization tool, since the panel keeps the two
    // as independent selection slots.
    const streamDiscoverySelection = useCallback((category: string, id: string | null) => {
        const streamCategory = category === 'modules' ? 'tools' : category;
        let payload: object | null = null;
        if (id) {
            if (category === 'printing') payload = DISCOVERY_PRINTING_PAYLOADS[id] ?? null;
            else if (category === 'pricing') payload = DISCOVERY_PRICING_PAYLOADS[id] ?? null;
            else if (category === 'ai') payload = DISCOVERY_AI_PAYLOADS[id] ?? null;
            else if (category === 'commerce') payload = DISCOVERY_COMMERCE_PAYLOADS[id] ?? null;
            else if (category === 'tools' || category === 'modules')
                payload =
                    id === 'collage-layouts' ? (collagePayloadRef.current ?? DISCOVERY_TOOLS_PAYLOADS[id] ?? null) : (DISCOVERY_TOOLS_PAYLOADS[id] ?? null);
        }
        const message: ZakekeMessageDiscoveryModeUpdate = {
            zakekeMessageType: 'DiscoveryModeUpdate',
            category: streamCategory as ZakekeMessageDiscoveryModeUpdate['category'],
            payload
        };
        customizerIframeRef.current?.contentWindow?.postMessage(message, '*');
    }, []);

    // After a credit dialog grants/buys, tell the iframe to re-read its credit availability (so the just-
    // unlocked AI tools work without a reload) and then open the pending tool.
    const proceedWithPendingTool = useCallback(() => {
        customizerIframeRef.current?.contentWindow?.postMessage({ zakekeMessageType: 'DiscoveryModeRefreshCredits' }, '*');
        const tool = pendingAiToolRef.current;
        if (tool) streamDiscoverySelection('ai', tool);
    }, [streamDiscoverySelection]);

    // CASE 3: open the buy-credits dialog; on a successful purchase, re-resolve and open the pending tool.
    const openPurchaseDialog = useCallback(() => {
        showDialog(
            'discovery-ai-purchase',
            <DiscoveryAiCreditsPurchaseDialog
                onPurchased={async () => {
                    const state = await resolveAiCredits();
                    if (state.creditCase === 'has-credits') proceedWithPendingTool();
                }}
            />
        );
    }, [showDialog, resolveAiCredits, proceedWithPendingTool]);

    // CASE 1: the seller has never activated. Grant the free trial (auto-refill off) from the persistent AI
    // panel banner; on success re-resolve, drop the banner, and open any tool the seller had already engaged.
    const handleActivateCredits = useCallback(async () => {
        setIsActivatingCredits(true);
        const granted = await grantTrial();
        if (!granted) {
            setIsActivatingCredits(false);
            showNotification({ type: 'danger', message: T._('Could not activate free credits. Please try again.', 'Admin'), dismiss: { duration: 3000 } });
            return;
        }
        const state = await resolveAiCredits();
        setAiCreditCase(state.creditCase);
        setIsActivatingCredits(false);
        if (state.creditCase === 'has-credits') proceedWithPendingTool();
    }, [grantTrial, resolveAiCredits, proceedWithPendingTool]);

    // Fill Background legacy import: fire the SAME default-library import the setup page runs, directly from the
    // banner (no gallery-manager detour). Progress is the shared GalleryImportBlockingOverlay rendered below — we
    // just bump its refresh signal so it picks up the freshly-queued background task and shows its loading bar.
    const handleImportBackgroundGallery = useCallback(async () => {
        const queued = await startDefaultGalleryImport();
        if (!queued) {
            showNotification({
                type: 'danger',
                message: T._('Could not start the background gallery import. Please try again.', 'Admin'),
                dismiss: { duration: 3000 }
            });
            return;
        }
        setBgImportRefreshSignal((n) => n + 1);
    }, [startDefaultGalleryImport]);

    // The overlay reports the background import finished: the account now has a gallery, so remount the iframe →
    // the customizer re-fetches the now-populated gallery, and drop the banner (the seller is no longer legacy).
    const handleBgImportCompleted = useCallback(() => {
        setIsBgGalleryLegacy(false);
        reloadIframe();
    }, [reloadIframe]);

    // Engage an AI tool: gate the real tool on the credit case resolved at this exact moment. CASE 2
    // (has-credits) streams straight through — the customizer shows its own "AI tools consume credits"
    // confirmation before running (useAICreditConfirmation), so the host doesn't duplicate it here.
    const engageAiTool = useCallback(
        async (id: string) => {
            pendingAiToolRef.current = id;
            const state = await resolveAiCredits();
            setAiCreditCase(state.creditCase);
            // CASE 1 no longer opens a modal: the persistent panel banner (shown while needs-activation) is the
            // activation path. We keep the engaged tool in pendingAiToolRef so activating from the banner opens it.
            if (state.creditCase === 'has-credits') streamDiscoverySelection('ai', id);
            else if (state.creditCase === 'out-of-credits') openPurchaseDialog();
        },
        [resolveAiCredits, streamDiscoverySelection, openPurchaseDialog]
    );

    // Open the buy-credits dialog for the VECTORIZE pool. A purchase reboots the iframe for the same reason
    // activation does (see handleActivateVectorizeCredits).
    const openVectorizePurchaseDialog = useCallback(() => {
        showDialog(
            'discovery-vectorize-purchase',
            <DiscoveryAiCreditsPurchaseDialog
                service='VECTORIZE'
                title={T._('Buy AI Vectorizer credits', 'Admin')}
                intro={T._(
                    "You've used all your AI Vectorizer credits. Purchase a credit pack to keep vectorizing images, here in the preview and in your live store.",
                    'Admin'
                )}
                successMessage={T._('Credits added. You can now use the AI Vectorizer.', 'Admin')}
                onPurchased={async () => {
                    setVectorizeCredits(await resolveVectorizeCredits());
                    reloadIframe();
                }}
            />
        );
    }, [showDialog, resolveVectorizeCredits, reloadIframe]);

    // Engage the AI Vectorizer printing method: resolve the VECTORIZE balance and stream the print type ONLY
    // when there are credits to spend. Without them the customizer's mapper drops `vectorizeSettings` (it gates
    // on servicesEnabled.VECTORIZE), so streaming anyway would lock the side to a method that silently does
    // nothing. The pill still shows as selected and the banner explains what to do — same shape as engageAiTool.
    const engageVectorizePrintMethod = useCallback(
        async (id: string) => {
            // Plan can't use it at all: don't even query the balance — the banner's only offer is an upgrade.
            if (isVectorizerUnavailableRef.current) {
                streamDiscoverySelection('printing', null);
                return;
            }
            const state = await resolveVectorizeCredits();
            setVectorizeCredits(state);
            // Clear rather than leave the previously streamed method applied: the panel already shows AI
            // Vectorizer as the selected pill, so keeping the old print type live would contradict it.
            streamDiscoverySelection('printing', state.creditCase === 'has-credits' ? id : null);
        },
        [resolveVectorizeCredits, streamDiscoverySelection]
    );

    // Grant the one-time VECTORIZE trial from the banner, then REBOOT the iframe. The reboot is what makes the
    // new credits visible: the customizer derives `vectorizeSettings` from `servicesEnabled.VECTORIZE` through
    // a module-level product cache that a live credit refresh would not invalidate, so a fresh boot is the
    // reliable way to pick them up. Nothing is lost — the seller has only just pressed the pill. Once it comes
    // back interactive the replay effect re-engages the method, now resolving to has-credits.
    const handleActivateVectorizeCredits = useCallback(async () => {
        setIsActivatingVectorizeCredits(true);
        const granted = await grantVectorizeTrial();
        if (!granted) {
            setIsActivatingVectorizeCredits(false);
            showNotification({ type: 'danger', message: T._('Could not activate free credits. Please try again.', 'Admin'), dismiss: { duration: 3000 } });
            return;
        }
        setVectorizeCredits(await resolveVectorizeCredits());
        setIsActivatingVectorizeCredits(false);
        reloadIframe();
    }, [grantVectorizeTrial, resolveVectorizeCredits, reloadIframe]);

    const handleDiscoverySelect = useCallback(
        (category: string, id: string | null) => {
            setDiscoverySelections((prev) => ({ ...prev, [category]: id }));

            // AI Creative Tools are gated on the seller's credit case at the moment of engagement: has-credits
            // streams straight through (real tool), otherwise a credit dialog opens first.
            if (category === 'ai') {
                if (id) void engageAiTool(id);
                else streamDiscoverySelection('ai', null);
                return;
            }

            // The AI Vectorizer printing method is credit-gated the same way (its own VECTORIZE pool), so it
            // takes the same detour instead of streaming straight through.
            if (category === 'printing' && id === AI_VECTORIZER_PILL) {
                void engageVectorizePrintMethod(id);
                return;
            }

            // Fill Background (Tools): resolve whether the seller is a legacy account with no background gallery,
            // so the panel can offer the default-gallery import — the customizer's gallery tab renders empty
            // otherwise. Any seller with a default or personalized gallery loads real images live (no banner).
            if (category === 'tools' && id === 'fill-background') {
                void resolveBgGalleryLegacy().then(setIsBgGalleryLegacy);
            }

            // Non-AI selections stream immediately via the unified streamer. Each wired button maps to a
            // payload: printing → a raw print-type, pricing → a sample pricing rule, tools/modules → a tool
            // marker. Deselecting (or a not-yet-implemented button) resolves to null = "no override".
            streamDiscoverySelection(category, id);
        },
        [engageAiTool, engageVectorizePrintMethod, streamDiscoverySelection, resolveBgGalleryLegacy]
    );

    // Simulated order quantity for the discovery sandbox (no footer quantity selector without a product
    // quantityRule). Streamed live to the customizer, which applies it via setQuantity so quantity-based
    // sample rules (quantity-tiers) re-tier without a reload. Restored from cache alongside the selections.
    const [discoveryQuantity, setDiscoveryQuantity] = useState(() => (canRestoreDiscovery && discoverySelectionsCache ? discoverySelectionsCache.quantity : 1));

    const handleDiscoveryQuantityChange = useCallback((qty: number) => {
        setDiscoveryQuantity(qty);
        const message: ZakekeMessageDiscoveryModeUpdate = {
            zakekeMessageType: 'DiscoveryModeUpdate',
            category: 'quantity',
            payload: qty
        };
        customizerIframeRef.current?.contentWindow?.postMessage(message, '*');
    }, []);

    // Keep the module-level cache in sync so returning to this product (SPA navigation) restores the same
    // selections. A full page reload re-initializes the module and clears it — the intended reset.
    useEffect(() => {
        discoverySelectionsCache = {
            productId,
            selections: discoverySelections,
            quantity: discoveryQuantity,
            entryCategory: seedCategory,
            entryTool: seedTool
        };
    }, [productId, discoverySelections, discoveryQuantity, seedCategory, seedTool]);

    // Latest selections/quantity read by the replay effect without making it depend on (and re-run for) every
    // individual selection change — it should re-apply only when the customizer (re)becomes interactive.
    const discoveryStateRef = useRef({ selections: discoverySelections, quantity: discoveryQuantity });
    discoveryStateRef.current = { selections: discoverySelections, quantity: discoveryQuantity };

    // Sync discovery state to URL search params so browser back restores it
    useEffect(() => {
        const params = new URLSearchParams(searchParams.toString());
        let changed = false;

        // mode
        if (discoveryMode) {
            if (params.get('mode') !== 'discovery') {
                params.set('mode', 'discovery');
                changed = true;
            }
        } else {
            if (params.has('mode')) {
                params.delete('mode');
                changed = true;
            }
        }

        // Per-category selections are intentionally NOT persisted to the URL (see the state init above): they
        // reset on reload to stay coherent with the freshly-booted iframe. Strip any stale selection params.
        for (const key of ['printing', 'pricing', 'tools', 'ai', 'commerce', 'modules']) {
            if (params.has(key)) {
                params.delete(key);
                changed = true;
            }
        }

        if (changed) {
            const newSearch = params.toString();
            const newPath = `${location.pathname}${newSearch ? '?' + newSearch : ''}`;
            navigate(newPath, { replace: true });
        }
    }, [discoveryMode]);

    // Queries
    const {
        addons,
        model,
        overviewSubscription,
        testOrderNumberInBillingDate,
        subscription,
        salesChannels = [],
        standaloneSites = [],
        shopifyAuthEcommerceSettings,
        sellerUserInterfaces
    } = useLazyLoadQuery<testPageQuery>(
        graphql`
            query testPageQuery(
                $productId: ID!
                $canAccessOrders: Boolean!
                $canReadChannels: Boolean!
                $canReadStandaloneSites: Boolean!
                $canReadSubscription: Boolean!
            ) {
                addons {
                    addonID
                    billingSystemAddonID
                    description
                    name
                    periodUnit
                    code
                    published
                    price
                    sellerID
                    rates {
                        feeValue
                    }
                }
                overviewSubscription @include(if: $canReadSubscription) {
                    chargeBeeSubscription {
                        currencyCode
                    }
                }
                model(id: $productId) {
                    id
                    name
                    code
                    provider
                    statusID
                    isDemo
                    printTypes {
                        printTypeID
                    }
                    colors {
                        colorID
                        name
                        code
                        colorCode
                    }
                    iframeUrl {
                        testUrl
                        copyUrl
                    }
                    salesChannel {
                        id
                        ecommerceSettings {
                            ecommerceName
                            remoteUrl
                            productsApi
                            hasProductsApi
                            isMarketplace
                            definitiveUrl
                        }
                    }
                    zakekeEnvironment
                    canAddAdditionalCode
                    additionalCodes {
                        code
                        salesChannelID
                        merchantProductName
                        salesChannel {
                            id
                            ecommerceSettings {
                                ecommerceName
                                remoteUrl
                                definitiveUrl
                            }
                        }
                    }
                    modelColorMappings {
                        colorID
                        salesChannelID
                        merchantCode
                    }
                }
                testOrderNumberInBillingDate @include(if: $canAccessOrders)
                subscription {
                    maxProducts {
                        pricingVersion
                        numPublishedProducts
                        maxProducts
                        numProducts
                    }
                    maxNumTestOrders @include(if: $canAccessOrders)
                    isOverMaxNumTestOrders @include(if: $canAccessOrders)
                    inTrial
                }
                salesChannels(status: ACTIVE) @include(if: $canReadChannels) {
                    id
                    ecommerceSettings {
                        ecommerceID
                        ecommerceName
                        isMarketplace
                        hasProductsApi
                        remoteUrl
                        definitiveUrl
                        currencyCode
                    }
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                }
                standaloneSites @include(if: $canReadStandaloneSites) {
                    salesChannelGlobalID
                }
                shopifyAuthEcommerceSettings {
                    isTestStore
                }
                sellerUserInterfaces(first: 50, environment: CUSTOMIZER) {
                    nodes {
                        isDefault
                        userInterfaceId
                    }
                }
            }
        `,
        { productId: activeProductId, canAccessOrders, canReadChannels, canReadStandaloneSites, canReadSubscription },
        { fetchKey, fetchPolicy: 'network-only' }
    );

    if (!model) {
        throw new Error(`Oops: product not found!`);
    }

    const isPublished = model.statusID === 1;
    const shopifySalesChannelIDs = [model.salesChannel, ...model.additionalCodes.map((code) => code.salesChannel)]
        .filter((channel) => channel.ecommerceSettings?.ecommerceName === 'SHOPIFY')
        .map((channel) => channel.id);
    const isUnpublishedStandalone = !isPublished && model.salesChannel.ecommerceSettings?.ecommerceName === 'NONE';
    const hasOnlineStore = salesChannels.some((channel) => (channel.ecommerceSettings?.ecommerceName ?? 'NONE') !== 'NONE');
    const addonsCurrency = overviewSubscription?.chargeBeeSubscription?.currencyCode ?? salesChannels.at(0)?.ecommerceSettings?.currencyCode ?? 'USD';

    // The two refusals the AI Vectorizer activation would hit on the backend, mirrored from the print-method
    // save path (see checkForVectorizerSettingsBeforeSave in printType.tsx): the plan must support vectorization,
    // and a Shopify development store cannot use it in production. The pill stays VISIBLE either way — discovery
    // exists to show merchants what they don't have yet — but selecting it offers an upgrade instead of an
    // activation that the backend would refuse.
    const isVectorizerSupportedByPlan = sellerSubscription?.planVersion === 'VERSION_2' && (sellerSubscription?.plan?.additionalFeatures.vectorization ?? true);
    const isVectorizerUnavailable = !isVectorizerSupportedByPlan || ((shopifyAuthEcommerceSettings?.isTestStore ?? false) && config.isProduction);
    isVectorizerUnavailableRef.current = isVectorizerUnavailable;
    const wasPublishedRef = useRef(isPublished);
    const [publishedInThisSession, setPublishedInThisSession] = useState(false);

    useEffect(() => {
        if (isPublished && !wasPublishedRef.current) {
            setPublishedInThisSession(true);
            setIsShopifyDuplicationWarningVisible(true);
            localStorage.removeItem('testPageShopifyDuplicationWarningDismissed');
        }

        wasPublishedRef.current = isPublished;
    }, [isPublished]);

    const isOldUserInterface = useMemo(() => {
        const defaultUI = sellerUserInterfaces?.nodes?.find((ui) => ui?.isDefault);
        return defaultUI?.userInterfaceId === ENV_TO_OLD_USER_INTERFACE_ID.CUSTOMIZER;
    }, [sellerUserInterfaces]);

    useEffect(() => {
        if (hasResumed) return;

        const { doPublishImmediately, productID } = location.state || {};
        if (!doPublishImmediately || !productID || !model) return;

        handleSetStatusClick(model);

        setHasResumed(true);
    }, [hasResumed, location.state, model, navigate]);

    useEffect(() => {
        const publishDestination = searchParams.get('publish');
        if (publishDestination === 'convert' && !isStandaloneConversionEnabled) return;

        if (publishDestination === 'true' || publishDestination === 'store' || publishDestination === 'mini-site' || publishDestination === 'convert') {
            handleSetStatusClick(model, publishDestination === 'convert' ? 'CONVERT' : publishDestination === 'store' ? 'STORE' : undefined);
        }
    }, [searchParams, model, isStandaloneConversionEnabled]);

    // Mutations
    const [commitTestOrder] = useMutation<testPageOrderMutation>(graphql`
        mutation testPageOrderMutation($designDocID: String, $compositionDocID: String, $details: [TestOrderDetailInput!]) {
            createTestOrder(designDocID: $designDocID, compositionDocID: $compositionDocID, details: $details) {
                orderID
            }
        }
    `);

    const [setStatusProduct, isSettingProductStatus] = useMutation<testPageProductSetStatusMutation>(graphql`
        mutation testPageProductSetStatusMutation($input: SetStatusProductInput!) {
            setStatusProduct(input: $input) {
                model {
                    id
                    statusID
                }
                errors {
                    ... on Error {
                        message
                    }
                }
            }
        }
    `);

    const [commitDuplicate, isInFightDuplicate] = useMutation<testPageDuplicateMutation>(graphql`
        mutation testPageDuplicateMutation($id: ID!, $targets: [ModelDuplicateTargetInput!]!) {
            duplicateModel(id: $id, targets: $targets) {
                productIDs
                resultMessage
                status
            }
        }
    `);

    const [commitConnectModel, isInFightConnectModel] = useMutation<testPageConnectModelMutation>(graphql`
        mutation testPageConnectModelMutation($id: ID!, $target: ModelDuplicateTargetInput!, $replaceStandalone: Boolean!) {
            connectModel(id: $id, target: $target, replaceStandalone: $replaceStandalone)
        }
    `);

    const [deleteProduct, isInFightDelete] = useMutation<testPageDeleteMutation>(graphql`
        mutation testPageDeleteMutation($productID: String!) {
            deleteProduct(productID: $productID) {
                id
            }
        }
    `);

    // Hooks
    const openTestOrdersPurchaseDialog = useCallback(() => {
        // Return to this exact test product page after the (Shopify) charge is approved, stripping any
        // approval markers already present so they don't accumulate across attempts.
        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.delete('charge_id');
        returnUrl.searchParams.delete('forTEST_ORDERS');

        showDialog(
            'test-orders-purchase',
            <TestOrdersPurchaseDialog
                returnUrlAfterApproval={returnUrl.toString()}
                // Reload the iframe so it leaves the stuck placing-order state and returns to its initial state.
                onClose={reloadIframe}
                onPurchased={() => {
                    // The effective Test Orders limit is computed by the backend from the subscription and
                    // non-expired purchase history. Recreate the Relay environment so every subscription query
                    // drops its pre-purchase snapshot; changing only this query's fetchKey can leave another
                    // shared subscription record serving the stale isOverMaxNumTestOrders value.
                    incrementStoreKey();
                    reloadIframe();
                    startTransition(() => setFetchKey((prev) => prev + 1));
                }}
            />
        );
    }, [incrementStoreKey, showDialog, reloadIframe, startTransition, setFetchKey]);

    const onMessageReceivedFromIframe = useCallback(
        (event: MessageEvent) => {
            const isPreviewIframe = event.source === customizerIframeRef.current?.contentWindow || event.source === composerIframeRef.current?.contentWindow;
            if (!isPreviewIframe) return;

            const data = event.data;
            const isSpecializedAddToCart = isCustomizerSpecializedAddToCartMessage(data);
            const isAddToCart = isComposerAddToCartMessage(data) || isCustomizerAddToCartMessage(data) || isSpecializedAddToCart;

            if (isAddToCart && !canAccessOrders) {
                showErrorNotification({ message: T._('You do not have permission to create test orders.', 'Admin') });
                reloadIframe();
                return;
            }

            // No test orders left: prompt the seller to buy more instead of placing the order.
            if (isAddToCart && subscription?.isOverMaxNumTestOrders) {
                openTestOrdersPurchaseDialog();
                return;
            }

            // Composer add-to-cart: { zakekeMessageType: "AddToCart", message: { composition, preview, quantity } }
            if (isComposerAddToCartMessage(data)) {
                commitTestOrder({
                    variables: {
                        compositionDocID: data.message.composition
                    },
                    onCompleted: ({ createTestOrder }) => {
                        navigate(backofficeUrl(`/order/${createTestOrder?.orderID}`));
                    }
                });
                return;
            }

            // Customizer add-to-cart: { zakekeMessageType: 0, designId: "..." }
            if (isCustomizerAddToCartMessage(data)) {
                commitTestOrder({
                    variables: {
                        designDocID: data.designId
                    },
                    onCompleted: ({ createTestOrder }) => {
                        navigate(backofficeUrl(`/order/${createTestOrder?.orderID}`));
                    }
                });
                return;
            }

            if (isSpecializedAddToCart) {
                const details = getCustomizerTestOrderDetails(data);
                if (!details) {
                    showErrorNotification({ message: T._('The test order data is invalid. Please try again.', 'Admin') });
                    reloadIframe();
                    return;
                }

                commitTestOrder({
                    variables: {
                        designDocID: data.data.designID,
                        details
                    },
                    onCompleted: ({ createTestOrder }) => {
                        navigate(backofficeUrl(`/order/${createTestOrder?.orderID}`));
                    },
                    onError: () => {
                        showErrorNotification({ message: T._('Failed creating the test order. Please try again.', 'Admin') });
                        reloadIframe();
                    }
                });
            }
        },
        [canAccessOrders, commitTestOrder, navigate, openTestOrdersPurchaseDialog, reloadIframe, subscription?.isOverMaxNumTestOrders]
    );

    useEffect(() => {
        window.addEventListener('message', onMessageReceivedFromIframe);
        return () => window.removeEventListener('message', onMessageReceivedFromIframe);
    }, [onMessageReceivedFromIframe]);

    // Returning from the Shopify approval page for a test orders purchase: reopen the dialog so the
    // ServiceCredits flow detects the charge_id and finalizes the purchase on this same page.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('charge_id') && params.get('forTEST_ORDERS')) {
            openTestOrdersPurchaseDialog();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for OPEN_AI_STUDIO postMessage from the customizer iframe
    useEffect(() => {
        const handleOpenAIStudio = (event: MessageEvent) => {
            if (event.data?.type === 'OPEN_AI_STUDIO') {
                const aiProductId = parseInt(event.data?.params?.productId, 10);
                const variantId = event.data?.params?.variantId ? parseInt(event.data?.params?.variantId, 10) : undefined;
                const docId = event.data?.params?.docId || undefined;
                const sidesData = event.data?.params?.sides;
                const model3DParam = event.data?.params?.model3D;
                const model3D = typeof model3DParam === 'string' || (typeof Blob !== 'undefined' && model3DParam instanceof Blob) ? model3DParam : undefined;
                const model3DRootUrl = typeof event.data?.params?.rootUrl === 'string' ? event.data.params.rootUrl : undefined;

                // Convert sides data to ProductSideImage array
                let sideImages: ProductSideImage[] | undefined;
                if (sidesData && sidesData.length > 0) {
                    sideImages = sidesData.map((side: { sideId: number; sideName: string; preview: string }) => ({
                        sideId: side.sideId,
                        sideName: side.sideName,
                        url: side.preview
                    }));
                }

                if (aiProductId) {
                    showDialog(
                        'ai-studio-dialog',
                        <AIStudioDialog
                            productId={aiProductId}
                            variantId={variantId}
                            sideImages={sideImages}
                            docId={docId}
                            model3D={model3D}
                            model3DRootUrl={model3DRootUrl}
                            onClose={() => closeDialog('ai-studio-dialog')}
                        />
                    );
                }
            }
        };

        window.addEventListener('message', handleOpenAIStudio);
        return () => window.removeEventListener('message', handleOpenAIStudio);
    }, [productId, showDialog, closeDialog]);

    const handleSetStatusClick = (product: PreviewModel, destination?: 'STORE' | 'CONVERT', skipPlanLimit = false, skipStoreConnection = false) => {
        if (!product) return;
        const isConversion = destination === 'CONVERT';
        if (isConversion && (!isStandaloneConversionEnabled || product.statusID !== 2 || product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE'))
            return;
        if (!isAllowed(['PRODUCTS_PUBLISH'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }
        const isUnpublishedStandalone = product.statusID === 2 && product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE';
        const hasOnlineStore = salesChannels.some((channel) => (channel.ecommerceSettings?.ecommerceName ?? 'NONE') !== 'NONE');
        const hasMiniSite = isStandaloneEnabled && standaloneSites.some((site) => site.salesChannelGlobalID === product.salesChannel.id);

        const publishToMiniSite = isUnpublishedStandalone && hasMiniSite && !hasOnlineStore;
        const pricingVersion = subscription?.maxProducts.pricingVersion ?? 'VERSION_1';
        if (!skipPlanLimit && pricingVersion === 'VERSION_2' && product.statusID === 2) {
            const numCurrentPublishedProducts = subscription?.maxProducts.numPublishedProducts ?? 0;
            const numMaxPublishedProducts = subscription?.maxProducts.maxProducts ?? 0;

            if (!hasOnlineStore && !publishToMiniSite) {
                showDialog(
                    'complete-ecommerce-integration',
                    <NoIntegrationDialog
                        redirectPage={removeCultureAdmin()}
                        productId={toLocalID(productId) ?? 0}
                        subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                        message={
                            <label>
                                {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                            </label>
                        }
                    ></NoIntegrationDialog>
                );
                return;
            } else if (numCurrentPublishedProducts >= numMaxPublishedProducts) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        marketPlace={sellerMarketplace ?? undefined}
                        message={
                            <>
                                {stringFormat(
                                    T._(
                                        `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                        'Admin'
                                    ),
                                    numMaxPublishedProducts.toString()
                                )}
                            </>
                        }
                        onUpgradeCompleted={async () => {
                            handleSetStatusClick(product, destination, true);
                        }}
                    />
                );

                return;
            }
        }

        if (
            product?.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
            hasOnlineStore &&
            !publishToMiniSite &&
            product?.statusID === 2 &&
            !skipStoreConnection &&
            (isConversion || (product.additionalCodes.length === 0 && product.modelColorMappings.length === 0))
        ) {
            const realSalesChannels = salesChannels.filter((s) => (s.ecommerceSettings?.ecommerceName ?? 'NONE') !== 'NONE');
            showDialog(
                'connectProductDialog',
                <ConvertStandaloneProductDialog
                    productID={product.id}
                    linkedStoreCodes={product.additionalCodes
                        .filter((code) => code.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE')
                        .map((code) => ({ code: code.code, salesChannelID: code.salesChannelID, merchantProductName: code.merchantProductName ?? '' }))}
                    additionalCodes={product.additionalCodes.map((c) => {
                        return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName };
                    })}
                    productColors={product.colors as ProductColor[]}
                    merchantProductOptionsConnected={[]}
                    salesChannels={realSalesChannels}
                    productSalesChannelID={product.salesChannel.id}
                    replaceStandaloneProduct={isConversion}
                    convert={isConversion}
                    onConfirmMappingVariants={() => {
                        closeDialog('connectProductDialog');
                        handleSetStatusClick(product, destination, true, true);
                    }}
                    onConfirm={async (sourceProduct, salesChannelId) => {
                        if (!product) {
                            showDialog('no-selection', <MessageDialog message={T._('Select a product', 'Admin')} />);
                            return;
                        }

                        if (!sourceProduct) return;

                        closeDialog('connectProductDialog');

                        const loadingNotification = showNotification({
                            type: 'default',
                            message: T._('Publishing your customizable product...', 'Admin')
                        });

                        let response!: testPageConnectModelMutation$data;

                        try {
                            // Save product
                            response = await new Promise((resolve, reject) =>
                                commitConnectModel({
                                    onCompleted: (response) => {
                                        resolve(response);
                                    },
                                    onError: (response) => reject(response),
                                    updater: (store) => {
                                        store.invalidateStore();
                                    },
                                    variables: {
                                        id: model!.id,
                                        target: {
                                            name: sourceProduct!.name,
                                            sku: sourceProduct!.sku,
                                            imageUrl: sourceProduct!.thumbnail,
                                            salesChannelId
                                        },
                                        replaceStandalone: isConversion
                                    }
                                })
                            );
                            showNotification({
                                type: 'success',
                                message: T._('Product connected and published!', 'Admin'),
                                dismiss: {
                                    duration: 3000
                                }
                            });

                            refreshAfterPublish();
                        } catch (ex: any) {
                            showNotification({
                                type: 'danger',
                                message: (
                                    <>
                                        {T._('Failed connecting and publishing product.', 'Admin')}
                                        <br />
                                        {ex.errors?.[0].extensions?.message}
                                    </>
                                ),
                                dismiss: {
                                    duration: 3000
                                }
                            });
                        }
                        removeNotification(loadingNotification);
                    }}
                    forPublishingProduct={true}
                />
            );

            return;
        } else if (!hasOnlineStore && !publishToMiniSite && product.statusID !== 1) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    redirectPage={removeCultureAdmin()}
                    productId={toLocalID(productId) ?? 0}
                    subTitleNoIntegration={T._('Do you want to start selling your customizable products at your store?', 'Admin')}
                    message={
                        <label>{T._('Complete the integration with Zakeke by selecting your ecommerce platform and connecting your store.', 'Admin')}</label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        }

        if (!showCopyUrl) {
            showInfoNotification({ message: product!.statusID === 1 ? T._('Unpublishing product', 'Admin') : T._('Publishing product', 'Admin') });
        }
        setStatusProduct({
            variables: {
                input: {
                    productID: product!.id!,
                    status: product!.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                }
            },
            onError: () => {
                showErrorNotification({ message: product!.statusID === 1 ? T._('Failed Unpublish product', 'Admin') : T._('Failed Publish product', 'Admin') });
            },
            onCompleted: (response, errors) => {
                if (!showCopyUrl) {
                    showSuccessNotification({ message: product!.statusID === 1 ? T._('Product unpublished', 'Admin') : T._('Product published', 'Admin') });
                }
                refreshAfterPublish();
            }
        });
    };

    const refreshAfterPublish = () => {
        const updatedSearchParams = new URLSearchParams(searchParams.toString());
        updatedSearchParams.delete('publish');

        const newSearch = updatedSearchParams.toString();
        const newPath = `${location.pathname}${newSearch ? '?' + newSearch : ''}`;

        startTransition(() => setFetchKey((prev) => prev + 1));
        navigate(newPath, { replace: true });
    };

    function removeCultureAdmin() {
        const { pathname, search } = window.location;
        const pattern = /^\/[^/]+\/Admin(?=\/|$)/i;

        const cleanedPath = pathname.replace(pattern, '/').replace(/\/+/g, '/');
        return `${cleanedPath}${search}`;
    }

    const handleAddSalesChannels = (modelID: string, salesChannelID: string) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        let realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');

        // if (product.sourceProductTemplateID) {
        if (realSalesChannels.length == 0) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                    message={
                        <label>
                            {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                        </label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        } else if (realSalesChannels.length == 1) {
            shopifyAuthEcommerceSettings?.isTestStore
                ? showDialog(
                      'add-sales-channel-not-available',
                      <MessageDialog
                          windowDecorator={SalesChannelNotAvailableDialogWindow}
                          message={
                              <>
                                  {T._(
                                      "We're sorry, but you cannot add sales channels at this time. Your account is currently in trial mode on Shopify or is a development store.",
                                      'Admin'
                                  )}
                                  <br />
                                  <br />
                                  {T._('To access this feature, please upgrade to an active Shopify plan.', 'Admin')}
                                  <br />
                                  <br />
                                  {T._('Thank you for your understanding.', 'Admin')}
                              </>
                          }
                          buttons={[
                              {
                                  label: T._('OK', 'Admin'),
                                  onClick: () => {
                                      closeDialog('add-sales-channel-not-available');
                                  }
                              }
                          ]}
                      />
                  )
                : showDialog(
                      'add-sales-channel',
                      <AddSalesChannelDialog
                          onSalesChannelCreated={() => {
                              closeDialog('add-sales-channel');
                          }}
                      ></AddSalesChannelDialog>
                  );
        } else {
            let availableSalesChannels = realSalesChannels.filter(
                (rs) => rs.id != model.salesChannel.id && !model.additionalCodes.some((ac) => ac.salesChannelID == rs.id)
            );

            if (availableSalesChannels.length == 0) {
                showNotification({
                    type: 'danger',
                    message: T._('The product has been connected with all sales channels.', 'Admin'),
                    dismiss: {
                        duration: 3000
                    }
                });
                return;
            }

            showDialog(
                'connectProductDialog',
                <Suspense
                    fallback={
                        <DialogOverlayConnect>
                            <Loader />
                        </DialogOverlayConnect>
                    }
                >
                    <ConnectModelDialog
                        productID={'guid://Zakeke/' + uuid() + '/' + toLocalID(model.id)}
                        forEditProduct={false}
                        forAddingNewSalesChannel={true}
                        merchantProductOptionsConnected={[]}
                        additionalCodes={model.additionalCodes.map((c) => {
                            return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName ?? '' };
                        })}
                        productColors={model.colors as ProductColor[]}
                        salesChannels={availableSalesChannels}
                        productSalesChannelID={model.salesChannel.id}
                        onConfirm={async (sourceProduct, salesChannelId) => {
                            if (!model) {
                                showDialog('no-selection', <MessageDialog message={T._('Select a product', 'Admin')} />);
                                return;
                            }

                            if (!sourceProduct) return;

                            closeDialog('connectProductDialog');

                            const loadingNotification = showNotification({
                                type: 'default',
                                message: T._('Publishing your customizable product...', 'Admin')
                            });

                            let response!: productListConnectModelMutation$data;

                            try {
                                // Save product
                                response = await new Promise((resolve, reject) =>
                                    commitConnectModel({
                                        onCompleted: (response) => resolve(response),
                                        onError: (response) => reject(response),
                                        updater: (store) => {
                                            store.invalidateStore();
                                        },
                                        variables: {
                                            id: model.id,
                                            target: {
                                                name: sourceProduct!.name,
                                                sku: sourceProduct!.sku,
                                                imageUrl: sourceProduct!.thumbnail,
                                                salesChannelId
                                            },
                                            replaceStandalone: false
                                        }
                                    })
                                );

                                showNotification({
                                    type: 'success',
                                    message: T._('Product connected and published!', 'Admin'),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                            } catch (ex: any) {
                                showNotification({
                                    type: 'danger',
                                    message: (
                                        <>
                                            {T._('Failed connecting and publishing product.', 'Admin')}
                                            <br />
                                            {ex.errors?.[0].extensions?.message}
                                        </>
                                    ),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                            }
                            removeNotification(loadingNotification);
                        }}
                    />
                </Suspense>
            );
        }
    };

    const handleDuplicate = (model: PreviewModel) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog(
            'duplicate',
            <DuplicateDialog
                sourceProductCode={model!.code}
                salesChannelID={model!.salesChannel.id}
                onConfirm={async (targets, destinationSalesChannelID) => {
                    closeDialog('duplicate');

                    const loadingNotification = showNotification({
                        type: 'default',
                        message: T._('The product is duplicating...', 'Admin')
                    });

                    let response!: testPageDuplicateMutation$data;

                    try {
                        // Save product
                        response = await new Promise<testPageDuplicateMutation$data>((resolve, reject) =>
                            commitDuplicate({
                                onCompleted: (response) => resolve(response),
                                onError: (response) => reject(response),
                                updater: (store) => {
                                    store.invalidateStore();
                                },
                                variables: {
                                    id: model!.id,
                                    targets: targets.map((x) => ({
                                        name: x.name!,
                                        sku: x.sku!,
                                        imageUrl: x.thumbnail,
                                        salesChannelId: destinationSalesChannelID
                                    }))
                                }
                            })
                        );
                        if (response.duplicateModel.status === 'WARNING') {
                            showWarningNotification({
                                message: T._('There is already a duplicating product request in progress. Please wait for it to complete.', 'Admin')
                            });
                        } else {
                            showNotification({
                                type: 'success',
                                message: T._('Products duplicated!', 'Admin'),
                                dismiss: {
                                    duration: 3000
                                }
                            });
                            window.location.href = backofficeUrl('/products');
                        }
                    } catch (ex: any) {
                        showNotification({
                            type: 'danger',
                            message: (
                                <>
                                    {T._('Failed duplicating products.', 'Admin')}
                                    <br />
                                    {ex.errors?.[0].extensions?.message}
                                </>
                            ),
                            dismiss: {
                                duration: 3000
                            }
                        });
                    }
                    removeNotification(loadingNotification);
                }}
            />
        );
    };
    const handleDeleteClick = (model: PreviewModel) => {
        if (!isAllowed(['PRODUCTS_DELETE'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog(
            'delete-product',
            <QuestionDialog
                message={stringFormat(T._('Do you want delete the product "{0}"?', 'Admin'), model!.name)}
                onYesClick={() => {
                    closeDialog('delete-product');
                    showInfoNotification({
                        message: T._('Please wait. The product is deleting..', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                    deleteProduct({
                        variables: {
                            productID: model!.id
                        },
                        onError: () => {
                            showErrorNotification({ message: T._('Failed deleting product', 'Admin') });
                        },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Product deleted.', 'Admin') });
                            incrementStoreKey();
                            navigate(backofficeUrl('/products'));
                        },
                        updater: (store) => store.invalidateStore()
                    });
                }}
            />
        );
    };

    const handleMarketplacePreviewClick = (productID: string) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog('marketplace-preview-url', <MarketplacePreviewUrlDialog productID={productID} />);
    };

    const composerIframeRef = useRef<HTMLIFrameElement>(null);
    const customizerIframeRef = useRef<HTMLIFrameElement>(null);
    const discoveryReadyRef = useRef(false);
    const handleOpenAIStudioClick = () => {
        customizerIframeRef.current?.contentWindow?.postMessage({ zakekeMessageType: 'OpenAIStudio' }, '*');
    };

    // Whether the customizer is fully loaded AND (if prompted) a variant has been chosen — gates the
    // discovery tool buttons. Driven by the customizer's DiscoveryModeInteractive beacon; reset to false
    // on every iframe remount (product switch / mode toggle / reload) so the buttons re-disable while the
    // fresh customizer boots.
    const [discoveryInteractive, setDiscoveryInteractive] = useState(false);
    // Ids of AI/Tools discovery pills the customizer reports as already active on the opened product; the
    // panel hides them. Driven by the DiscoveryModeHiddenTools beacon; reset to [] on every iframe remount.
    const [discoveryHiddenTools, setDiscoveryHiddenTools] = useState<string[]>([]);
    // Keeps the current array when it is already empty: a fresh [] is a new state and would re-render the page.
    const resetDiscoveryHiddenTools = useCallback(() => setDiscoveryHiddenTools((current) => (current.length === 0 ? current : [])), []);
    const isComposer = model.zakekeEnvironment === 'COMPOSER';

    // DALLO SCENE EDITOR SOTTO PROVA C’È LA SCENA, NON IL PRODOTTO. Chi entra in test mode da lì vuole
    // guardare come si comporta la scena: il configuratore gli mostra il visore 3D coi comandi che ci
    // stanno dentro — barra 3D, AR, VTO — e lascia fuori la colonna delle opzioni e il footer del
    // commercio, che appartengono alla prova del prodotto e qui coprirebbero metà di quel che si è
    // venuti a vedere.
    //
    // L'INDIRIZZO DI RITORNO È L'UNICO TESTIMONE di dove si è partiti: la test mode è una sola per
    // tutta la piattaforma e non porta con sé altro.
    const fromSceneEditor = (searchParams.get('returnTo') ?? '').includes(SCENE_EDITOR_PAGE);

    // SI TORNA DA DOVE SI È PARTITI. Test mode è una sola per tutta la piattaforma e ci si entra da
    // due ambienti — il Composer e lo Scene Editor — con lo stesso tasto: un ritorno cablato sul
    // Composer riporta nella stanza sbagliata chi è partito dall'altra, e con l'etichetta glielo
    // annuncia pure. Il `returnTo` il tasto lo manda già; qui basta ascoltarlo.
    //
    // E L'ETICHETTA GUARDA L'INDIRIZZO, non l'ambiente del prodotto: promettere il Composer e
    // portare altrove è peggio che dire soltanto «indietro». Il Composer resta il ripiego per chi
    // arriva senza `returnTo` — un segnalibro, un link vecchio.
    const composerExit = useMemo(() => {
        const returnTo = searchParams.get('returnTo');
        const composerUrl = `${backofficeUrl('/product/3D-product-configurator-composer')}?${new URLSearchParams({
            product_id: activeProductId
        }).toString()}`;

        // «BACK TO EDITOR» PER TUTTI E DUE (utente, 2026-10-21): la parola è una, come nel prototipo; quale editor lo
        // dice il tooltip.
        const backToEditor = T._('Back to editor', 'Admin');
        if (!returnTo) return { url: composerUrl, label: backToEditor, tip: stringFormat(T._('Back to {0}', 'Admin'), productLogicName()) };
        if (returnTo.includes(SCENE_EDITOR_PAGE)) return { url: returnTo, label: backToEditor, tip: T._('Back to Scene Editor', 'Admin') };
        if (returnTo.includes('3D-product-configurator-composer'))
            return { url: returnTo, label: backToEditor, tip: stringFormat(T._('Back to {0}', 'Admin'), productLogicName()) };
        return { url: returnTo, label: T._('Back', 'Admin'), tip: undefined };
    }, [searchParams, activeProductId]);
    const { copyDemoProduct, isCopyingDemoProduct, copyDemoProductElement } = useCopyDemoProduct((modelID) => {
        if (modelID == null) return;
        const newProductId = String(modelID);
        startTransition(() => {
            setActiveProductId(newProductId);
        });
        navigate(backofficeUrl(`/test-mode/${newProductId}`), { replace: true });
    });

    const handleEditProduct = () => {
        if (isComposer) {
            window.location.href = `/Composer?id=${toLocalID(model.id)}&page=product`;
        } else {
            navigate(backofficeUrl('/Product?product_id=' + toLocalID(model.id)));
        }
    };
    const getBaseIframeUrl = useCallback(
        (testUrl: string) => {
            let url = testUrl;
            // Configurator (COMPOSER) products use the same test-mode contract as the Customizer:
            // keep the preview editable, reserve viewerMode for orders, and identify this BO surface
            // with testing=1. This path receives its URL from ProductService, not ComposerService.
            if (isComposer) {
                // Match the Full Editor's local development override. Otherwise a local
                // Composer silently tests the deployed theme instead of the current fixes.
                if (['localhost', '127.0.0.1'].includes(window.location.hostname) && config.newConfiguratorBaseUrl) {
                    const localUrl = new URL(config.newConfiguratorBaseUrl);
                    const remoteUrl = new URL(url);
                    localUrl.search = remoteUrl.search;
                    localUrl.hash = remoteUrl.hash;
                    url = localUrl.toString();
                }
                url = withForcedNewInterface(removeIframeSearchParam(url, 'viewerMode'));
                url = setIframeSearchParam(url, 'testing', '1');
                // Sotto-modo della test mode, come `discoverymode`: senza `testing` non significa nulla,
                // e il configuratore lo tratta infatti come un no-op.
                if (fromSceneEditor) url = setIframeSearchParam(url, 'sceneonly', '1');
            }
            // The customizer's "Place test order" (add-to-cart) stays always visible. When the seller
            // has no test orders left we intercept the add-to-cart message and prompt them to buy more.
            if (isPrototypeMode) {
                url = setIframeSearchParam(url, 'isPrototype', '1');
            }
            return url;
        },
        [isComposer, isPrototypeMode, fromSceneEditor]
    );
    const baseIframeUrl = useMemo(() => getBaseIframeUrl(model.iframeUrl.testUrl), [getBaseIframeUrl, model.iframeUrl.testUrl]);

    useEffect(() => {
        if (discoveryMode) return undefined;

        const onTestModeMessage = (event: MessageEvent) => {
            const iframe = isComposer ? composerIframeRef.current : customizerIframeRef.current;
            if (!iframe || event.source !== iframe.contentWindow || !isTestModeReadyMessage(event.data)) return;

            postAddonsList(iframe, addons, addonsCurrency);
        };

        window.addEventListener('message', onTestModeMessage);
        return () => window.removeEventListener('message', onTestModeMessage);
    }, [addons, addonsCurrency, discoveryMode, isComposer]);

    // Composer products: send postMessage to load the product in the iframe
    useEffect(() => {
        if (!isComposer) return;

        const sendLoadMessage = () => {
            composerIframeRef.current?.contentWindow?.postMessage(
                {
                    type: 'load',
                    parameters: {
                        name: model.name,
                        token: config.token,
                        ecommerce: 'api',
                        integrationVersion: 2,
                        currency: 'USD',
                        culture: config.culture || 'en',
                        modelCode: model.code,
                        qty: 1,
                        attributes: []
                    }
                },
                '*'
            );
        };

        const interval = setInterval(sendLoadMessage, 500);

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.zakekeType === 'loaded') {
                clearInterval(interval);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            clearInterval(interval);
            window.removeEventListener('message', handleMessage);
        };
    }, [isComposer, model.name, model.code]);

    // Configurator (COMPOSER) products and the old customizer UI don't support Discovery Mode: force Test Mode only,
    // even if the URL arrived with ?mode=discovery. The Test/Discovery switch is also hidden in these cases.
    useEffect(() => {
        if ((isComposer || isOldUserInterface) && discoveryMode) {
            setDiscoveryMode(false);
        }
    }, [isComposer, isOldUserInterface, discoveryMode]);

    // Entering discovery mode reloads the iframe with discoverymode=1: the URL change flows into
    // the iframe `key` below, remounting it = a clean restart that boots the customizer in
    // discovery mode. Per-category selections are NOT applied through the URL anymore — once
    // booted, the customizer listens for DiscoveryModeUpdate postMessages (no reload per click).
    const iframeSrc = useMemo(() => {
        const base = baseIframeUrl;
        if (!base) return base;
        if (!discoveryMode) return base;
        return setIframeSearchParam(base, 'discoverymode', '1');
    }, [baseIframeUrl, discoveryMode]);

    // Discovery mode handshake: the customizer announces readiness (DiscoveryModeReady) once it has
    // booted in discovery mode — this just establishes the channel. It later beacons DiscoveryModeInteractive
    // once it is truly ready for input (fully loaded and, if prompted, a variant chosen); that boolean drives
    // whether the DiscoveryModePanel tool buttons are enabled.
    useEffect(() => {
        if (!discoveryMode) {
            discoveryReadyRef.current = false;
            setDiscoveryInteractive(false);
            resetDiscoveryHiddenTools();
            return undefined;
        }
        const onDiscoveryMessage = (event: MessageEvent) => {
            if (!customizerIframeRef.current || event.source !== customizerIframeRef.current.contentWindow) return;

            if (isDiscoveryModeReadyMessage(event.data)) {
                discoveryReadyRef.current = true;
                postAddonsList(customizerIframeRef.current, addons, addonsCurrency);
            } else if (isDiscoveryModeInteractiveMessage(event.data)) {
                setDiscoveryInteractive(event.data.interactive);
            } else if (isDiscoveryModeHiddenToolsMessage(event.data)) {
                setDiscoveryHiddenTools(event.data.hiddenTools);
            } else if (isDiscoveryModeActivateToolMessage(event.data)) {
                // The merchant clicked "enable this on my product" inside a discovery tool dialog — either Get
                // a Quote's "Not available in Test Mode" warning or one of the explainer-only tools, which have
                // no sandbox and whose dialog IS the whole experience. Route to that tool's real setup page
                // with the sandbox product pre-assigned where the page reads it — the same destination as the
                // panel's Activate button.
                const productLocalId = toLocalID(activeProductId);
                const withProduct = productLocalId ? `?fromProductId=${productLocalId}` : '';
                const toolRoutes: Record<string, string> = {
                    'get-a-quote': `/tools/get-a-quote/quote-rule${withProduct}`,
                    // The explainer-only tools take the sandbox product directly rather than through
                    // ?fromProductId, each in its own convention: templates by path segment, 3D by
                    // ?product_id (the same targets their own list pages navigate to — see
                    // productTemplates.handleProductSelected / preview3DProducts.handle3DClick). Without a
                    // resolvable local id they fall back to the product picker. Profanity rules are
                    // account-level, so that one has no per-product page at all.
                    'pre-designed-templates': productLocalId
                        ? `/tools/template-product-selection/templates/${productLocalId}`
                        : '/tools/template-product-selection',
                    '3d-preview-ar': productLocalId ? `/3Dpreview-and-AR/?product_id=${productLocalId}` : '/3Dpreview-and-AR/products',
                    'profanity-filter': '/tools/profanity-filter-and-image-moderator'
                };
                navigate(backofficeUrl(toolRoutes[event.data.tool] ?? '/tools'));
            } else if (isDiscoveryAiGenerationCompletedMessage(event.data)) {
                // A real AI generation just consumed a credit. Re-resolve and, if the shared pool is now
                // spent, open the buy-credits dialog so the seller can top up (they can keep the tool open).
                void resolveAiCredits().then((state) => {
                    setAiCreditCase(state.creditCase);
                    if (state.creditCase === 'out-of-credits') openPurchaseDialog();
                });
            }
        };
        window.addEventListener('message', onDiscoveryMessage);
        return () => window.removeEventListener('message', onDiscoveryMessage);
    }, [addons, addonsCurrency, discoveryMode, resolveAiCredits, openPurchaseDialog, navigate, activeProductId]);

    // Any change to the iframe `key` below (product switch, discovery toggle, resize, manual reload)
    // remounts the iframe = a fresh customizer boot, so the previous "interactive" state is stale. Re-disable
    // the tool buttons until the newly booted customizer beacons DiscoveryModeInteractive again.
    useEffect(() => {
        setDiscoveryInteractive(false);
        resetDiscoveryHiddenTools();
    }, [iframeSrc, effectivePreviewSize, iframeReloadNonce]);

    // Once the freshly-booted customizer beacons interactive, re-apply the restored selections so the live
    // preview matches the Active chips (e.g. after a Save redirect + browser Back rebooted the iframe clean).
    // Re-applying the same overrides is idempotent, so this is also safe when interactive re-toggles (e.g. a
    // variant dialog reopening then closing). A restored AI selection is re-gated through the credit flow
    // (engageAiTool) rather than streamed directly, so re-entry still respects the seller's credit case; the
    // AI Vectorizer printing method goes back through its own gate for the same reason — and that is also what
    // applies the method automatically after activating its credits rebooted the iframe.
    useEffect(() => {
        if (!discoveryMode || !discoveryInteractive) return;
        const { selections, quantity } = discoveryStateRef.current;
        let hasSelection = false;
        for (const [category, id] of Object.entries(selections)) {
            if (id) {
                if (category === 'ai') void engageAiTool(id);
                else if (category === 'printing' && id === AI_VECTORIZER_PILL) void engageVectorizePrintMethod(id);
                else streamDiscoverySelection(category, id);
                hasSelection = true;
            }
        }
        // Quantity only matters for the quantity-tiers rule; re-send it only when that rule is active.
        if (hasSelection && selections.pricing === 'quantity-tiers' && quantity !== 1) {
            customizerIframeRef.current?.contentWindow?.postMessage(
                { zakekeMessageType: 'DiscoveryModeUpdate', category: 'quantity', payload: quantity } as ZakekeMessageDiscoveryModeUpdate,
                '*'
            );
        }
    }, [discoveryMode, discoveryInteractive, streamDiscoverySelection, engageAiTool, engageVectorizePrintMethod]);

    // Resolve the VECTORIZE balance as soon as discovery opens, so the AI Vectorizer banner is already showing
    // the right thing (activate / how many credits are left / buy more) before the seller presses the pill —
    // the point of this feature is that the cost is visible BEFORE anything is spent. Skipped when the pill is
    // hidden anyway. Re-resolved on every pill engagement and after each credit mutation.
    useEffect(() => {
        if (!discoveryMode || isVectorizerUnavailable) return;
        void resolveVectorizeCredits().then(setVectorizeCredits);
    }, [discoveryMode, isVectorizerUnavailable, resolveVectorizeCredits]);
    useAutoAddonActivation(customizerIframeRef, !isComposer);

    return (
        <FullscreenLayout>
            {copyDemoProductElement}
            <FullscreenIframeTopBar $discovery={discoveryMode}>
                <FullscreenIframeTopBarLeft>
                    {!isPrototypeMode && (
                        <ZTooltip position='bottom' title={T._('Switch product', 'Admin')} enterDelay={0} exitDelay={0} interactive={false}>
                            <ChangeProductButton
                                $discovery={discoveryMode}
                                onClick={() => {
                                    showDialog(
                                        'product-catalog',
                                        <ProductCatalogDialog
                                            onConfirm={(modelID) => {
                                                closeDialog('product-catalog');
                                                const newProductId = String(modelID);
                                                startTransition(() => {
                                                    setActiveProductId(newProductId);
                                                });
                                                navigate(backofficeUrl(`/test-mode/${newProductId}`), { replace: true });
                                            }}
                                        />
                                    );
                                }}
                            >
                                <Icon>
                                    <ChangeProductIcon />
                                </Icon>
                            </ChangeProductButton>
                        </ZTooltip>
                    )}
                    <FullscreenIframeTitle $discovery={discoveryMode}>{model.name}</FullscreenIframeTitle>
                    {!isPrototypeMode && !discoveryMode && !model.isDemo ? (
                        <>
                            <BadgeContainer>
                                <StyledBadge
                                    color={model.statusID === 1 ? '#065f46' : '#374151'}
                                    badgeColor={model.statusID === 1 ? '#d6f3e2' : '#f3f4f6'}
                                    showCircle={false}
                                >
                                    {model.statusID === 1 ? T._('Online', 'Admin') : T._('Not published', 'Admin')}
                                </StyledBadge>
                            </BadgeContainer>
                            <PublishSwitchWrapper>
                                <ZSwitch
                                    checked={isPublished}
                                    onChange={() => handleSetStatusClick(model)}
                                    disabled={!isAllowed(['PRODUCTS_PUBLISH']) || isSettingProductStatus || isInFightConnectModel}
                                />
                                <PublishSwitchLabel>{isPublished ? T._('Published', 'Admin') : T._('Unpublished', 'Admin')}</PublishSwitchLabel>
                                {isUnpublishedStandalone && hasOnlineStore && isStandaloneConversionEnabled && (
                                    <BasicButton
                                        $size='small'
                                        disabled={!isAllowed(['PRODUCTS_PUBLISH']) || isSettingProductStatus || isInFightConnectModel}
                                        onClick={() => handleSetStatusClick(model, 'CONVERT')}
                                    >
                                        {T._('Convert to a store product', 'Admin')}
                                    </BasicButton>
                                )}
                            </PublishSwitchWrapper>
                            {isPublished && model.salesChannel.ecommerceSettings?.definitiveUrl && (
                                <Link href={model.salesChannel.ecommerceSettings.definitiveUrl} target='_blank'>
                                    <Icon>
                                        <ExternalLinkIcon />
                                    </Icon>
                                </Link>
                            )}
                        </>
                    ) : null}
                </FullscreenIframeTopBarLeft>
                <FullscreenIframeTopBarCenter>
                    {!isPrototypeMode && isDiscoveryModeEnabled && !isComposer && !isOldUserInterface && (
                        <ModeToggleContainer>
                            <ModeToggleButton
                                $active={!discoveryMode}
                                $variant='test'
                                onClick={() => {
                                    setDiscoveryMode(false);
                                    setDiscoverySelections({ ...EMPTY_DISCOVERY_SELECTIONS });
                                    setDiscoveryQuantity(1);
                                    discoverySelectionsCache = null;
                                }}
                            >
                                {T._('Test Mode', 'Admin')}
                            </ModeToggleButton>
                            <ModeToggleButton $active={discoveryMode} $variant='discovery' onClick={() => setDiscoveryMode(true)}>
                                {T._('Discovery Mode', 'Admin')}
                            </ModeToggleButton>
                        </ModeToggleContainer>
                    )}
                </FullscreenIframeTopBarCenter>
                <FullscreenIframeTopBarRight>
                    {canAccessOrders &&
                        !isComposer &&
                        subscription?.maxNumTestOrders != null &&
                        subscription?.inTrial === false &&
                        shopifyAuthEcommerceSettings?.isTestStore !== true && (
                            <ZTooltip position='bottom' title={T._('The test order limit resets at the start of each subscription billing period.', 'Admin')}>
                                {subscription.isOverMaxNumTestOrders ? (
                                    <ClickableCounterBadge
                                        warning={true}
                                        onClick={openTestOrdersPurchaseDialog}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                openTestOrdersPurchaseDialog();
                                            }
                                        }}
                                        role='button'
                                        tabIndex={0}
                                    >
                                        {T._('No Test Orders left. Buy more.', 'Admin')}
                                    </ClickableCounterBadge>
                                ) : (
                                    <CounterBadge
                                        items={[
                                            {
                                                label: T._('Test orders:', 'Admin'),
                                                value: Math.max(subscription.maxNumTestOrders - (testOrderNumberInBillingDate ?? 0), 0)
                                            }
                                        ]}
                                    />
                                )}
                            </ZTooltip>
                        )}
                    {!isPrototypeMode && !isOldUserInterface && !discoveryMode && (
                        <ZTooltip
                            position='bottom'
                            title={
                                previewSize === 'desktop-wide'
                                    ? T._('Desktop', 'Admin')
                                    : previewSize === 'desktop'
                                      ? T._('Laptop', 'Admin')
                                      : T._('Mobile', 'Admin')
                            }
                        >
                            <RoundIconButton
                                type='button'
                                aria-label={T._('Preview layout', 'Admin')}
                                onClick={() =>
                                    setPreviewSize((prev) => {
                                        const order: LivePreviewSize[] = ['desktop-wide', 'desktop', 'mobile'];
                                        return order[(order.indexOf(prev) + 1) % order.length];
                                    })
                                }
                            >
                                <Icon>
                                    {previewSize === 'desktop-wide' ? <DesktopWideIcon /> : previewSize === 'desktop' ? <DesktopIcon /> : <MobileIcon />}
                                </Icon>
                            </RoundIconButton>
                        </ZTooltip>
                    )}
                    {!isPrototypeMode && !discoveryMode && !isComposer && !model.isDemo && (
                        <ZTooltip position='bottom' title={T._('Product details', 'Admin')}>
                            <RoundIconButton type='button' aria-label={T._('Product details', 'Admin')} onClick={() => setShowSidebar(true)}>
                                <Icon>
                                    <ProductDetailsIcon />
                                </Icon>
                            </RoundIconButton>
                        </ZTooltip>
                    )}
                    {!isPrototypeMode && !discoveryMode && (
                        <DropdownActions
                            header={
                                <RoundIconButton type='button' aria-label={T._('Actions', 'Admin')}>
                                    <Icon>
                                        <VerticalThreePoint />
                                    </Icon>
                                </RoundIconButton>
                            }
                            items={
                                model.isDemo
                                    ? [
                                          <DropdownActionItem onClick={handleEditProduct}>{T._('Edit & Copy', 'Admin')}</DropdownActionItem>,
                                          canCopyDemoProduct(model.zakekeEnvironment) && (
                                              <DropdownActionItem onClick={() => copyDemoProduct(model.id)} disabled={isCopyingDemoProduct}>
                                                  {T._('Copy', 'Admin')}
                                              </DropdownActionItem>
                                          ),
                                          !isComposer && (
                                              <DropdownActionItem onClick={handleOpenAIStudioClick}>{T._('Open AI Studio', 'Admin')}</DropdownActionItem>
                                          ),
                                          <DropdownDivider />,
                                          <DropdownActionItem onClick={() => handleDeleteClick(model)} danger>
                                              {T._('Delete', 'Admin')}
                                          </DropdownActionItem>
                                      ]
                                    : [
                                          !isComposer && (
                                              <DropdownActionItem onClick={() => setShowSidebar(true)}>{T._('Product details', 'Admin')}</DropdownActionItem>
                                          ),
                                          <DropdownActionItem onClick={handleEditProduct}>{T._('Edit Product', 'Admin')}</DropdownActionItem>,
                                          model.provider !== 'INVITION' && model.provider !== 'CUSTOM_CAT' && model.provider !== 'PRINTFUL' && (
                                              <DropdownActionItem onClick={() => handleDuplicate(model)}>{T._('Duplicate', 'Admin')}</DropdownActionItem>
                                          ),
                                          ...(isComposer
                                              ? []
                                              : [
                                                    model.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' && model.canAddAdditionalCode && (
                                                        <DropdownActionItem onClick={() => handleAddSalesChannels(model.id, model.salesChannel.id)}>
                                                            {T._('Add to an online store', 'Admin')}
                                                        </DropdownActionItem>
                                                    ),
                                                    (model.salesChannel.ecommerceSettings?.ecommerceName === 'ETSY' ||
                                                        model.additionalCodes.some((c) => c.salesChannel.ecommerceSettings?.ecommerceName === 'ETSY')) && (
                                                        <DropdownActionItem
                                                            onClick={() => handleMarketplacePreviewClick(model!.id)}
                                                            disabled={model.statusID !== 1}
                                                        >
                                                            {T._('Generate a shareable demo product URL', 'Admin')}
                                                        </DropdownActionItem>
                                                    )
                                                ]),
                                          !isComposer && (
                                              <DropdownActionItem onClick={handleOpenAIStudioClick}>{T._('Open AI Studio', 'Admin')}</DropdownActionItem>
                                          ),
                                          <DropdownDivider />,
                                          <DropdownActionItem onClick={() => handleDeleteClick(model)} danger>
                                              {T._('Delete', 'Admin')}
                                          </DropdownActionItem>
                                      ]
                            }
                            orientation={'RIGHT'}
                        />
                    )}
                    {!isPrototypeMode && discoveryMode && (
                        <UISettingsButton onClick={() => navigate(backofficeUrl('/settings/UserInterface'))}>
                            {T._('User Interface Settings', 'Admin')}
                        </UISettingsButton>
                    )}
                    {isComposer && !isClassic ? (
                        <ZTooltip title={composerExit.tip} position='bottom'>
                            <BackToEditorButton onClick={() => navigate(composerExit.url)}>
                                <Icon>
                                    <ArrowBackIcon />
                                </Icon>
                                {composerExit.label}
                            </BackToEditorButton>
                        </ZTooltip>
                    ) : (
                        <FullscreenIframeCloseButton
                            onClick={() => {
                                // Completing the configuration wizard returns to the product list.
                                if (isClassic) {
                                    navigate(backofficeUrl('/products'), { replace: true });
                                    return;
                                }
                                // Other entries return to their explicit destination or the previous page.
                                const returnTo = searchParams.get('returnTo');
                                if (returnTo) navigate(returnTo);
                                else if (window.history.length > 1) navigate(-1);
                                else navigate(backofficeUrl('/products'));
                            }}
                            aria-label={T._('Close', 'Admin')}
                        >
                            <Icon>
                                <CloseIcon />
                            </Icon>
                        </FullscreenIframeCloseButton>
                    )}
                </FullscreenIframeTopBarRight>
            </FullscreenIframeTopBar>

            {isPublished && shopifySalesChannelIDs.length > 0 && !isPrototypeMode && !discoveryMode && !isComposer && !currentIAMUser.isIAMUser && (
                <TestModeShopifyAppEmbedBanner key={model.id} salesChannelIDs={shopifySalesChannelIDs} />
            )}

            {(isClassic || publishedInThisSession) &&
                !isPrototypeMode &&
                !discoveryMode &&
                !isComposer &&
                isShopifyDuplicationWarningVisible &&
                model.salesChannel.ecommerceSettings?.ecommerceName === 'SHOPIFY' && (
                    <BannerSection>
                        <NotificationBanner
                            type='WARNING'
                            title={getShopifyDuplicationWarningTitle()}
                            showCloseButton={true}
                            onClose={() => {
                                localStorage.setItem('testPageShopifyDuplicationWarningDismissed', 'true');
                                setIsShopifyDuplicationWarningVisible(false);
                            }}
                            content={getShopifyDuplicationWarningText()}
                            buttons={[
                                {
                                    label: T._('Learn more', 'Admin'),
                                    linkButton: true,
                                    href: SHOPIFY_DUPLICATION_WARNING_LEARN_MORE_URL
                                },
                                {
                                    label: T._('Change behavior', 'Admin'),
                                    onClick: () => navigate(backofficeUrl(SHOPIFY_INTEGRATION_SETTINGS_PATH))
                                }
                            ]}
                        />
                    </BannerSection>
                )}

            {!isPrototypeMode && !discoveryMode && !isComposer && !isOldUserInterface && isInfoBannerVisible && (
                <BannerSection>
                    <NotificationBanner
                        type='INFO'
                        showCloseButton={true}
                        onClose={() => {
                            localStorage.setItem('testPageInfoBannerDismissed', 'true');
                            setIsInfoBannerVisible(false);
                        }}
                        content={
                            <span>
                                {T._('This preview shows what your customers will see. Switch to', 'Admin')}{' '}
                                <Link onClick={() => setDiscoveryMode(true)}>{T._('Discovery Mode', 'Admin')}</Link>
                                {T._(' to explore and try new features such as', 'Admin')}{' '}
                                <Link onClick={() => navigate(backofficeUrl('/printing-methods'))}>{T._('Printing Methods', 'Admin')}</Link>
                                {T._(', ', 'Admin')}
                                <Link onClick={() => navigate(backofficeUrl('/pricings-rules'))}>{T._('Pricing Rules', 'Admin')}</Link>
                                {T._(', and other capabilities that are not currently active.', 'Admin')}
                            </span>
                        }
                    />
                </BannerSection>
            )}

            {!isPrototypeMode && discoveryMode && (
                <Suspense fallback={null}>
                    <DiscoveryCollagePrefetch onLoaded={(payload) => (collagePayloadRef.current = payload)} />
                </Suspense>
            )}

            {/* Fill Background legacy import progress — the SAME shared overlay/progress bar the gallery setup page
                uses. Kept mounted while the import banner shows (it self-hides until a background task is active),
                so pressing Import surfaces its loading bar in place, then reboots the iframe on completion. */}
            {discoveryMode && discoverySelections.tools === 'fill-background' && isBgGalleryLegacy && (
                <GalleryImportBlockingOverlay
                    target={IMPORT_TARGET_BACKGROUND}
                    refreshSignal={bgImportRefreshSignal}
                    onProgress={() => {}}
                    onCompleted={handleBgImportCompleted}
                />
            )}

            {!isPrototypeMode && discoveryMode && (
                <DiscoveryModePanel
                    customizerReady={discoveryInteractive}
                    hiddenTools={discoveryHiddenTools}
                    selections={discoverySelections}
                    onSelect={handleDiscoverySelect}
                    printingBanner={
                        discoverySelections.printing === AI_VECTORIZER_PILL && (isVectorizerUnavailable || vectorizeCredits) ? (
                            <DiscoveryVectorizeBanner
                                planUnsupported={isVectorizerUnavailable}
                                credits={vectorizeCredits}
                                onActivate={handleActivateVectorizeCredits}
                                isActivating={isActivatingVectorizeCredits}
                                onBuy={openVectorizePurchaseDialog}
                                onUpgrade={() => showPlanUpgradeDialog(T._('Your current plan does not include the AI Vectorizer.', 'Admin'))}
                            />
                        ) : null
                    }
                    aiBanner={
                        discoverySelections.ai !== null && aiCreditCase === 'needs-activation' ? (
                            <DiscoveryAiActivationBanner onActivate={handleActivateCredits} isActivating={isActivatingCredits} />
                        ) : null
                    }
                    toolsBanner={
                        discoverySelections.tools === 'fill-background' && isBgGalleryLegacy ? (
                            <DiscoveryFillBackgroundImportBanner onImport={handleImportBackgroundGallery} />
                        ) : null
                    }
                    quantity={discoveryQuantity}
                    quantityPresets={DISCOVERY_QUANTITY_PRESETS}
                    onQuantityChange={handleDiscoveryQuantityChange}
                    onSave={(category, item) => {
                        // Printing: an "informed" redirect to the print-method create page. Carry the
                        // streamed print-type payload in router state (the create form seeds every field
                        // from it via discoveryPrintTypeToSeed) and pre-assign the sandbox product through
                        // ?fromProductId (the numeric model id the form expects).
                        if (category === 'printing') {
                            const printPayload = DISCOVERY_PRINTING_PAYLOADS[item.id];
                            // Router state can be dropped by a culture/trailing-slash redirect on the way to the
                            // create page, which would leave it seeding a blank print method. Stash the payload in
                            // sessionStorage too; the create form reads it as a fallback and clears it.
                            try {
                                sessionStorage.setItem('discoveryPrintTypeSeed', JSON.stringify(printPayload));
                            } catch {
                                // Ignore storage failures — router state still carries the seed.
                            }
                            const productLocalId = toLocalID(activeProductId);
                            const target = productLocalId ? `/printing-method?fromProductId=${productLocalId}` : '/printing-method';
                            navigate(backofficeUrl(target), {
                                state: { discoveryPrintType: printPayload }
                            });
                            return;
                        }
                        // Pricing: same "informed redirect" as printing. Carry the streamed pricing-rule DSL in
                        // router state (the create form rebuilds the canonical rule from it via
                        // discoveryPricingRuleToSeed) and pre-assign the sandbox product through ?fromProductId.
                        if (category === 'pricing') {
                            const productLocalId = toLocalID(activeProductId);
                            const target = productLocalId ? `/pricing-rule?fromProductId=${productLocalId}` : '/pricing-rule';
                            navigate(backofficeUrl(target), {
                                state: { discoveryPricingRule: DISCOVERY_PRICING_PAYLOADS[item.id], discoveryPricingName: item.name }
                            });
                            return;
                        }
                        // Commerce rules: saving just opens each rule's real setup page (the preview itself is
                        // already streamed live for the rules that support it). None of these setup pages reads
                        // ?fromProductId to seed a product the way the printing/pricing create forms do, so
                        // route plainly.
                        if (category === 'commerce') {
                            const commerceRoutes: Record<string, string> = {
                                'extra-options': '/tools/extra-option/',
                                'profanity-filter': '/tools/profanity-filter-and-image-moderator',
                                'minimum-order-quantity': '/tools/moq',
                                'bulk-variation-forms': '/tools/bulk-variation-form/products'
                            };
                            navigate(backofficeUrl(commerceRoutes[item.id] ?? '/tools'));
                            return;
                        }
                        // Customization tools & Modules: route each activated item to its real create/setup
                        // page and pre-assign the sandbox product through ?fromProductId — the same convention
                        // printing/pricing use, which each create form already reads to seed its product
                        // association (see quoteRule/gridRule associatedProducts initializers). Previously the
                        // whole category fell through to `/tools?from=discovery&tool=…`; nothing consumed those
                        // params so it rendered the generic Tools menu (blank on a discovery account).
                        // Unlike printing/pricing there is no self-contained rule payload to carry here: a
                        // tool "rule" references catalog entities (shapes, grids) that only ever existed in
                        // the streamed customizer sandbox, so we seed the product only.
                        if (category === 'tools' || category === 'modules') {
                            const productLocalId = toLocalID(activeProductId);
                            const withProduct = productLocalId ? `?fromProductId=${productLocalId}` : '';
                            const toolRoutes: Record<string, string> = {
                                'get-a-quote': `/tools/get-a-quote/quote-rule${withProduct}`,
                                'collage-layouts': `/tools/collage-layouts/rule${withProduct}`,
                                // Fill Background: open the create-rule page pre-assigned to the sandbox product
                                // (areaBackgroundRuleContent reads ?fromProductId), same convention as above.
                                'fill-background': `/tools/fill-background/rule${withProduct}`,
                                // Shapes auto-assigns ?fromProductId only in embedded (chrome-less) mode, so
                                // open the full-page Shape Gallery for manual assignment rather than degrade
                                // the layout — no clean full-page product pre-assignment exists for it.
                                'shapes-and-masks': '/tools/shapes/my-gallery',
                                // Explainer-only tools: these two pre-assign the product through their own
                                // conventions rather than ?fromProductId (path segment / ?product_id). Same
                                // targets as the customizer dialog's activation link (see the
                                // DiscoveryModeActivateTool handler above) — keep the two in sync.
                                'pre-designed-templates': productLocalId
                                    ? `/tools/template-product-selection/templates/${productLocalId}`
                                    : '/tools/template-product-selection',
                                '3d-preview-ar': productLocalId ? `/3Dpreview-and-AR/?product_id=${productLocalId}` : '/3Dpreview-and-AR/products'
                            };
                            navigate(backofficeUrl(toolRoutes[item.id] ?? '/tools'));
                            return;
                        }
                        // AI Creative Tools: same treatment as customization tools above. This used to go to
                        // `/ai?from=discovery&tool=…` — nothing consumes those params, and `/ai` is only a
                        // legacy redirect to the generic `/ai-creative-tools` hub, which renders blank on a
                        // discovery account. Route each tool to its own real page instead, and for the two
                        // that are rule-based (lifestyle scenes, try-on) open the BUILDER with
                        // ?fromProductId so the new rule is pre-assigned to the sandbox product — those two
                        // pages read that param (aiProductStagingRule / aiVirtualModelRule). Image and
                        // design generation are account-level settings pages, so they route plainly.
                        if (category === 'ai') {
                            const productLocalId = toLocalID(activeProductId);
                            const withProduct = productLocalId ? `?fromProductId=${productLocalId}` : '';
                            const aiRoutes: Record<string, string> = {
                                'ai-image-generation': '/ai-creative-tools/image-generation-editing',
                                'ai-design-generation': '/ai-creative-tools/design-generation',
                                'ai-product-staging': `/ai-creative-tools/lifestyle-scenes-builder${withProduct}`,
                                'ai-virtual-try-on': `/ai-creative-tools/ai-tryon-builder${withProduct}`
                            };
                            navigate(backofficeUrl(aiRoutes[item.id] ?? '/ai-creative-tools'));
                            return;
                        }
                        navigate(backofficeUrl('/'));
                    }}
                    onBuildYourOwn={(category) => {
                        const routes: Record<string, string> = {
                            printing: '/printing-method',
                            pricing: '/pricings',
                            ai: '/ai-creative-tools',
                            tools: '/tools'
                        };
                        navigate(backofficeUrl(routes[category] ?? '/'));
                    }}
                />
            )}

            <FullscreenIframeContent>
                <PreviewFrameContainer ref={previewContainerRef}>
                    <WaitForProductExists productId={model.id} fallback={<TestPageSkeleton />}>
                        {effectivePreviewSize === 'desktop-wide' ? (
                            <FullscreenIframe
                                ref={isComposer ? composerIframeRef : customizerIframeRef}
                                key={`${iframeSrc}-${effectivePreviewSize}-${iframeReloadNonce}`}
                                src={iframeSrc}
                                allow='camera; microphone; autoplay; fullscreen; picture-in-picture'
                                allowFullScreen
                            />
                        ) : (
                            <PreviewSkin $width={skinConfig.width} $height={skinConfig.height} $scale={skinConfig.scale} $skin={skinConfig.skin}>
                                <FullscreenIframe
                                    ref={isComposer ? composerIframeRef : customizerIframeRef}
                                    key={`${iframeSrc}-${effectivePreviewSize}-${iframeReloadNonce}`}
                                    src={iframeSrc}
                                    allow='camera; microphone; autoplay; fullscreen; picture-in-picture'
                                    allowFullScreen
                                />
                            </PreviewSkin>
                        )}
                    </WaitForProductExists>
                </PreviewFrameContainer>
            </FullscreenIframeContent>

            {showSidebar && (
                <ProductSidePanel
                    productId={model.id}
                    hasOnlineStore={salesChannels.some((channel) => (channel.ecommerceSettings?.ecommerceName ?? 'NONE') !== 'NONE')}
                    onClose={() => setShowSidebar(false)}
                    onProductChange={refreshProductIframe}
                    onPreview={() => {
                        setShowSidebar(false);
                    }}
                    onPublishToggle={() => handleSetStatusClick(model)}
                    onConvertToStore={() => handleSetStatusClick(model, 'CONVERT')}
                    onDelete={() => {
                        setShowSidebar(false);
                        handleDeleteClick(model);
                    }}
                />
            )}
        </FullscreenLayout>
    );
};

export default TestPage;
